/**
 * Form Engine
 * Main form initialization and orchestration
 */

import { FormConfig } from "./form_config.js";
import { FormState } from "./form_state.js";
import { Security } from "./security.js";
import { FieldRenderer } from "../fields/field_renderer.js";
import { Validator } from "../validation/validator.js";
import { ConditionEngine } from "../conditions/condition_engine.js";
import { ValueRulesEngine } from "../conditions/value_rules_engine.js";
import { PageManager } from "../pages/page_manager.js";
import { PageNavigation } from "../pages/page_navigation.js";
import { FormSubmitter } from "../submission/form_submitter.js";
import { ErrorManager } from "../validation/error_manager.js";
import { LoadingSpinner } from "../ui/loading_spinner.js";
import { Branding } from "../ui/branding.js";
import { API } from "../utils/api.js";
import { FormatUtils } from "../utils/format_utils.js";
import { DateUtils } from "../utils/date_utils.js";
import { CalculatedFieldHelper } from "../utils/calculated_field_helper.js";
import { Translator } from "../utils/translator.js";
import { ShadowFieldHelper } from "../utils/shadow_field_helper.js";
import { FieldSyncHelper } from "../utils/field_sync_helper.js";
import { CustomersBasedFillHelper } from "../utils/customers_based_fill_helper.js";
import { IntlTelInputWrapper } from "../dependencies/intl_tel_input.js";
import { SignaturePadWrapper } from "../dependencies/signature_pad.js";
import { RepeaterHelper } from "../utils/repeater_helper.js";
import { PhoneUtils } from "../utils/phone_utils.js";
import { AITranslator } from "../utils/ai_translator.js";
import { loadBranchMap, getBranchNameFromMap } from "../utils/bank_helpers.js";
import {
  setup as setupIsraeliBankSelector,
  getBankSelectorIdForBranchField,
} from "../utils/israeli_bank_selector.js";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";

const TQTAG_FORM_FLATPICKR_CSS_ID = "tqtag-form-flatpickr-css";

/**
 * Ensure Flatpickr CSS is loaded so the date picker calendar is styled.
 * Injects a link to the bundle CSS (tqtag-form.css) if not already present.
 * @param {string} [cssUrl] - Optional URL for the form bundle CSS. If not set, derived from script src or default path.
 */
function ensureFlatpickrCss(cssUrl) {
  if (document.getElementById(TQTAG_FORM_FLATPICKR_CSS_ID)) return;
  let href = cssUrl;
  if (!href) {
    const script = document.querySelector('script[src*="tqtag-form.js"]');
    if (script && script.src) {
      href = script.src.replace(/\.js(\?.*)?$/i, ".css");
    } else {
      href = "/tqtag/js/form/dist/tqtag-form.css";
    }
  }
  const link = document.createElement("link");
  link.id = TQTAG_FORM_FLATPICKR_CSS_ID;
  link.rel = "stylesheet";
  link.href = href;
  document.head.appendChild(link);
}

export class FormEngine {
  constructor(formId, container, options = {}) {
    this.formId = formId;
    this.container = container;
    this.options = options;

    this.formState = new FormState();
    this.validator = null;
    this.conditionEngine = new ConditionEngine(this.container);
    this.valueRulesEngine = new ValueRulesEngine({
      container: this.container,
      onAfterSetSelect: (el) => this.updateNiceSelect(el),
    });
    this.pageManager = null;
    this.pageNavigation = null;
    this.formSubmitter = null;
    this.translator = new Translator();

    this.formConfig = null;
    this.activeCustomer = null;
    this.signaturePads = [];
    this.behalfContext = null;
    this.behalfToken = null;
    this.currentViewerUser = null;
    this.loadedEntryData = null;
    this.formSourceContext = null;
  }

  /**
   * Initialize form
   */
  async init() {
    try {
      LoadingSpinner.showInitial();

      // Check for behalf_token in URL query string
      const urlParams = new URLSearchParams(window.location.search);
      const behalfToken = urlParams.get("behalf_token");

      if (behalfToken) {
        this.behalfToken = behalfToken;

        // Validate behalf token before loading form
        try {
          const validationResponse = await API.validateBehalfToken(
            behalfToken,
            this.formId,
          );

          if (validationResponse.status !== "ok") {
            LoadingSpinner.hideInitial();

            // Show error and stop form rendering
            if (window.Swal) {
              window.Swal.fire({
                icon: "error",
                title: "Invalid Token",
                text:
                  validationResponse.error || "Invalid or expired behalf token",
              });
            } else {
              alert(
                validationResponse.error || "Invalid or expired behalf token",
              );
            }

            // Clear container to prevent form rendering
            if (this.container) {
              this.container.innerHTML = "";
            }
            return;
          }

          // Store behalf context
          this.behalfContext = {
            behalf_customer: validationResponse.behalf_customer,
            submitter_customer: validationResponse.submitter_customer,
            token_info: validationResponse.token_info,
          };
        } catch (error) {
          LoadingSpinner.hideInitial();

          // Show error and stop form rendering
          if (window.Swal) {
            window.Swal.fire({
              icon: "error",
              title: "Token Validation Failed",
              text: error.message || "Failed to validate behalf token",
            });
          } else {
            alert(error.message || "Failed to validate behalf token");
          }

          // Clear container to prevent form rendering
          if (this.container) {
            this.container.innerHTML = "";
          }
          return;
        }
      }

      // Load form configuration. Load current user from API only when backend hasn't provided customer (options.activeCustomer or window.TqTagFormActiveCustomer).
      const backendCustomer =
        this.options.activeCustomer ??
        (typeof window !== "undefined" ? window.TqTagFormActiveCustomer : null);
      const hasBackendCustomer =
        backendCustomer != null &&
        typeof backendCustomer === "object" &&
        Object.keys(backendCustomer).length > 0;
      const jwtGetter =
        this.options.getJwt != null ? this.options.getJwt : null;
      const [config, currentUser] = await Promise.all([
        FormConfig.load(this.formId, this.behalfToken),
        hasBackendCustomer
          ? Promise.resolve(null)
          : FormConfig.loadCurrentUser(jwtGetter),
      ]);

      this.currentViewerUser = currentUser;

      this.formConfig = config;
      if (hasBackendCustomer) {
        this.activeCustomer = backendCustomer;
      } else if (this.behalfContext && this.behalfContext.behalf_customer) {
        this.activeCustomer = this.behalfContext.behalf_customer;
      } else if (
        currentUser != null &&
        (currentUser.id != null || currentUser.customerID != null)
      ) {
        const customerId =
          currentUser.id != null ? currentUser.id : currentUser.customerID;
        try {
          const custRes = await API.getCustomerById(parseInt(customerId, 10));
          if (
            custRes.status === "ok" &&
            custRes.data &&
            custRes.data.length > 0
          ) {
            this.activeCustomer = custRes.data[0];
          } else {
            this.activeCustomer = currentUser;
          }
        } catch (e) {
          this.activeCustomer = currentUser;
        }
      } else {
        this.activeCustomer = currentUser;
      }

      // Build formSourceContext if any field uses source "another form (user filled)"
      const hasFormSourceField =
        this.formConfig.fields &&
        this.formConfig.fields.some((f) => f && f.field_sync_source === "form");
      if (hasFormSourceField) {
        try {
          const entriesResponse = await API.getFormEntriesCurrentUser(
            this.behalfToken || null,
            this.formId,
          );
          if (
            entriesResponse.status === "ok" &&
            Array.isArray(entriesResponse.entries)
          ) {
            const byFormId = {};
            entriesResponse.entries.forEach((entry) => {
              const formId = entry.form_id || entry.formId;
              if (!formId) return;
              const entryDate =
                entry.date != null
                  ? typeof entry.date === "number"
                    ? entry.date
                    : parseInt(entry.date, 10)
                  : 0;
              if (
                !byFormId[formId] ||
                (byFormId[formId].date != null &&
                  entryDate > (byFormId[formId].date || 0))
              ) {
                byFormId[formId] = entry;
              }
            });
            this.formSourceContext = byFormId;
          } else {
            this.formSourceContext = {};
          }
        } catch (err) {
          this.formSourceContext = {};
        }
      } else {
        this.formSourceContext = {};
      }

      // Set page title to form name (if available)
      if (this.formConfig?.title) {
        document.title = this.formConfig.title;

        // Populate h1 tag with form name if it exists on the page
        const h1Element = document.querySelector("h1");
        if (h1Element) {
          h1Element.textContent = this.formConfig.title;
        }
      }

      // Initialize AI Translator
      this.aiTranslator = new AITranslator(this.formId, this.formConfig);

      // Load translations based on form language
      const formLanguage = this.formConfig.language || "en";
      await this.translator.loadLanguage(formLanguage);

      // Validate configuration
      if (!FormConfig.validate(this.formConfig)) {
        throw new Error("Invalid form configuration");
      }

      // Determine mode: edit (entryId), draft list, or default (try to load draft when one exists)
      const entryId = this.options.entryId || Security.getEntryIdFromURL();
      const showDraftList = urlParams.get("draft_list") === "1";

      if (entryId) {
        this.formState.setEditMode(true, entryId);
      }

      // Initialize validator with translator and form config
      this.validator = new Validator(
        this.formState,
        this.translator,
        this.formConfig,
      );

      // Setup AI language selector (before rendering form)
      this.setupAILanguageSelector();

      ensureFlatpickrCss(this.options.formCssUrl);

      // 1. Draw form (with defaults from field config; no draft in scope)
      this.renderForm();
      this.initDatePickers();

      // Repeater handlers must be set before applyLoadedEntryData so addRows can call handleDuplicate
      RepeaterHelper.setup(this);

      // 2. Load draft/entry after DOM is ready, then apply (overwrites defaults)
      if (entryId) {
        await this.loadEntryData(entryId);
      } else if (!this.isPublicForm()) {
        if (showDraftList) {
          await this.showDraftList();
        } else {
          // Load last draft by default when one exists (no ?draft=1 needed)
          await this.loadLastDraft({ skipApply: true });
        }
      }

      await this.applyLoadedEntryData();

      // Setup conditions
      this.setupConditions();

      if (
        this.formConfig.field_value_rules &&
        this.formConfig.field_value_rules.length > 0
      ) {
        this.valueRulesEngine.setupValueRules(
          this.formConfig.field_value_rules,
          {
            formConfig: this.formConfig,
            formId: this.formId,
            container: this.container,
            onAfterSetSelect: (el) => this.updateNiceSelect(el),
          },
        );
      }

      // Setup pages
      this.setupPages();

      // Setup field handlers
      this.setupFieldHandlers();

      // Setup Israeli ID and phone validations
      this.setupIsraeliValidations();

      // Setup form submission
      this.setupFormSubmission();

      // Setup submit button
      this.setupSubmitButton();

      // Setup session timer
      this.setupSessionTimer();

      // Setup field-specific features
      this.setupFieldSpecificFeatures();

      // Setup auto-resize textarea
      this.setupAutoResizeTextarea();

      // Setup required attributes observer
      this.setupRequiredAttributesObserver();

      // Setup draft auto-save
      this.setupDraftAutoSave();

      // Apply form headers if they exist
      this.applyFormHeaders();
      this.applyOnePageAreaImage();

      // Strip required from checkbox groups again after init (in case another script or observer added it)
      setTimeout(() => this.stripRequiredFromCheckboxGroups(), 100);

      LoadingSpinner.hideInitial();
    } catch (error) {
      LoadingSpinner.hideInitial();

      // Handle access control errors
      if (error.errorType === "login_required") {
        this.displayAccessError(
          this.translator.t("validation.form_requires_login"),
        );
        return;
      } else if (error.errorType === "access_denied") {
        this.displayAccessError(this.translator.t("validation.access_denied"));
        return;
      }

      throw error;
    }
  }

  /**
   * Display access error message
   * @param {string} message - Error message to display
   */
  displayAccessError(message) {
    if (!this.container) return;

    this.container.innerHTML = `
      <div class="alert alert-warning" role="alert" style="margin: 20px; padding: 20px;">
        <h4 class="alert-heading"><i class="fas fa-exclamation-triangle me-2"></i>Access Denied</h4>
        <p>${FormatUtils.escapeHtml(message)}</p>
      </div>
    `;
  }

  /**
   * Load entry data for edit mode
   * @param {string} entryId - Entry ID
   */
  applyEntryMetadataToFormData(entryData, formConfig) {
    const base = { ...(entryData.form_data || entryData.formData || {}) };
    if (!formConfig?.fields) return base;

    formConfig.fields.forEach((field) => {
      if (!field?.entryMetadataSource || !field.id) return;
      const current = base[field.id];
      const isEmpty = (val) => {
        if (val == null || val === "") return true;
        if (Array.isArray(val)) return val.every((v) => v == null || v === "");
        return false;
      };
      if (field.entryMetadataSource === "creation_date" && !isEmpty(current)) {
        return;
      }
      const raw = this.resolveEntryMetadataValue(entryData, field.entryMetadataSource);
      if (!raw) return;
      const formatted = this.formatEntryMetadataForField(raw, field);
      if (!formatted) return;
      if (Array.isArray(current)) {
        const len = current.length > 0 ? current.length : 1;
        base[field.id] = Array.from({ length: len }, () => formatted);
      } else {
        base[field.id] = formatted;
      }
    });
    return base;
  }

  resolveEntryMetadataValue(entryData, source) {
    if (!source) return "";
    if (source === "creation_date") {
      return entryData.creation_date != null ? String(entryData.creation_date) : "";
    }
    if (source === "date") {
      return entryData.date != null ? String(entryData.date) : "";
    }
    if (source.indexOf("field_modified:") === 0) {
      const fieldId = source.substring("field_modified:".length);
      const dates = entryData.field_modified_dates || {};
      return dates[fieldId] != null ? String(dates[fieldId]) : "";
    }
    return "";
  }

  formatEntryMetadataForField(value, field) {
    if (!value) return "";
    const str = String(value);
    if (field.type === "date") {
      if (/^\d{4}-\d{2}-\d{2}T/.test(str)) return str;
      if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
      const ts = parseInt(value, 10);
      if (!isNaN(ts)) {
        return new Date(ts * 1000).toISOString();
      }
      const parsed = Date.parse(str);
      if (!isNaN(parsed)) {
        return new Date(parsed).toISOString();
      }
    }
    return str;
  }

  async loadEntryData(entryId) {
    try {
      const response = await API.loadFormEntry(entryId);
      if (response.status === "ok") {
        // The API returns { status: 'ok', data: {...} } where data contains the entry
        // Entry structure: { entry_id, form_id, date, tqtag_cust_id, created_by_cust_id,
        //                    last_updated_by_cust_id, form_data: {...} }
        const entryData = response.data || response.entry;

        if (entryData) {
          // Check if entry has last_updated_by_cust_id at the top level
          const lastUpdatedCustId = entryData.last_updated_by_cust_id;

          if (lastUpdatedCustId && parseInt(lastUpdatedCustId) > 0) {
            try {
              const customerResponse = await API.getCustomerById(
                parseInt(lastUpdatedCustId),
              );
              if (
                customerResponse.status === "ok" &&
                customerResponse.data &&
                customerResponse.data.length > 0
              ) {
                // Initialize behalfContext if it doesn't exist
                if (!this.behalfContext) {
                  this.behalfContext = {};
                }
                this.behalfContext.last_updated_customer =
                  customerResponse.data[0];
              }
            } catch (customerError) {
              // Continue without last_updated_customer if fetch fails
            }
          }

          // form_data contains the actual form field values
          const formData = this.applyEntryMetadataToFormData(
            entryData,
            this.formConfig,
          );
          this.formState.setFormData(formData);
          this.loadedEntryData = formData;
        }
      }
    } catch (error) {}
  }

  /**
   * Apply loaded entry data to rendered form (async when bank branch resolution is needed)
   */
  async applyLoadedEntryData() {
    if (!this.loadedEntryData) return;
    await this.populateFormData(this.loadedEntryData);
    this.loadedEntryData = null;
  }

  /**
   * Normalize checkbox/radio stored values into a comparable Set
   * @param {*} value - Stored value from entry
   * @param {{ splitCommas?: boolean }} [options] - splitCommas: false for radio (option text may contain commas)
   * @returns {Set<string>}
   */
  normalizeValuesForChecks(value, options = {}) {
    const splitCommas = options.splitCommas !== false;
    const toSet = (arr) =>
      new Set(
        (arr || [])
          .map((v) => (v === undefined || v === null ? "" : String(v)))
          .map((v) => v.trim())
          .filter((v) => v !== ""),
      );

    if (value === null || value === undefined) {
      return new Set();
    }

    if (Array.isArray(value)) {
      return toSet(value);
    }

    if (typeof value === "string") {
      // Try JSON array in string form
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return toSet(parsed);
        }
      } catch (e) {
        // Ignore parse errors; fall back to string handling
      }

      // Comma-separated multi-select (checkbox groups only; radio option labels often contain commas)
      if (splitCommas && value.includes(",")) {
        return toSet(value.split(","));
      }
      const trimmed = value.trim();
      return trimmed ? new Set([trimmed]) : new Set();
    }

    // Numbers/booleans or other primitive
    return new Set([String(value).trim()]);
  }

  /**
   * Populate form with data. Async when Israeli bank branch fields need BOI data.
   * @param {Object} formData - Form data
   * @returns {Promise<void>}
   */
  async populateFormData(formData) {
    const bankBranchPending = [];

    // Normalize: canonical is field_id (no []) with array value. Support legacy field_id[] for old drafts.
    const normalized = {};
    const seenBaseKeys = new Set();
    Object.keys(formData).forEach((key) => {
      if (key.endsWith("[]")) {
        const baseKey = key.slice(0, -2);
        const arr = formData[key];
        const scalar = formData[baseKey];
        if (Array.isArray(arr)) {
          normalized[baseKey] =
            scalar !== undefined && scalar !== null && scalar !== ""
              ? [scalar, ...arr]
              : arr;
        } else {
          normalized[baseKey] = formData[key];
        }
        seenBaseKeys.add(baseKey);
      } else if (!seenBaseKeys.has(key)) {
        // Canonical: field_id with scalar or array value (no [] in key)
        normalized[key] = formData[key];
      }
    });

    const scope = this.container || document;

    Object.keys(normalized).forEach((key) => {
      const value = normalized[key];
      const baseKey = key.replace(/\[\]$/, "");
      const fieldData = this.formConfig.fields.find((f) => f.id === baseKey);
      if (!fieldData) {
        return;
      }

      const isRepeaterArray = Array.isArray(value) && value.length > 0;

      if (isRepeaterArray) {
        // Repeater: indexed names field_id[0], field_id[1] or legacy field_id[]
        const selectorIndexed = `[name^="${key.replace(/["\\]/g, "\\$&")}["]`;
        const selectorLegacy = `[name="${key}[]"]`;
        let inputs = scope.querySelectorAll(selectorIndexed);
        if (inputs.length === 0)
          inputs = scope.querySelectorAll(selectorLegacy);

        if (inputs.length > 0) {
          const useIndexed =
            inputs.length > 0 &&
            inputs[0].name &&
            /\[\d+\]$/.test(inputs[0].name);
          const getRowCount = () => {
            const indices = new Set();
            inputs.forEach((inp) => {
              const m = inp.name && inp.name.match(/\[(\d+)\]$/);
              if (m) indices.add(parseInt(m[1], 10));
            });
            return indices.size > 0 ? Math.max(...indices) + 1 : inputs.length;
          };
          const currentRowCount = getRowCount();
          const needRows = value.length - currentRowCount;
          if (needRows > 0) {
            RepeaterHelper.addRows(inputs[0], needRows);
            inputs = scope.querySelectorAll(selectorIndexed);
            if (inputs.length === 0)
              inputs = scope.querySelectorAll(selectorLegacy);
          }
          const values = value;

          if (
            fieldData.type === "file" &&
            values.some((v) => v != null && String(v).trim() !== "")
          ) {
            values.forEach((fileRef, idx) => {
              const field = inputs[idx];
              if (!field) return;
              if (fileRef == null || String(fileRef).trim() === "") return;
              const fileLink = document.createElement("a");
              fileLink.href = fileRef;
              fileLink.target = "_blank";
              fileLink.className = "file_refernce";
              fileLink.setAttribute("data-field_id", key);
              fileLink.textContent =
                values.length > 1 ? `Open File ${idx + 1}` : "Open File";
              field.parentNode.insertBefore(fileLink, field.nextSibling);
              field.removeAttribute("required");
            });
          } else if (fieldData.type === "checkbox") {
            const areaScope =
              useIndexed && inputs[0] && inputs[0].closest(".container-fluid");
            const rows = areaScope
              ? areaScope.querySelectorAll(".colsListWrapper")
              : [];
            if (useIndexed) {
              // Repeater: one row per index, each row has its own set of selected values
              values.forEach((rowVal, idx) => {
                let rowInputs = [];
                if (rows.length > idx) {
                  const row = rows[idx];
                  const formCol =
                    row && row.querySelector('.formCol[data-id="' + key + '"]');
                  rowInputs = formCol
                    ? Array.from(
                        formCol.querySelectorAll('input[type="checkbox"]'),
                      )
                    : [];
                }
                if (rowInputs.length === 0) {
                  const rowInputName = `${key}[${idx}]`;
                  rowInputs = Array.from(
                    scope.querySelectorAll(
                      `[name="${rowInputName.replace(/["\\\[\]]/g, "\\$&")}"]`,
                    ),
                  );
                }
                if (rowInputs.length === 0) return;
                const normalizedSet = this.normalizeValuesForChecks(rowVal);
                rowInputs.forEach((fld) => {
                  fld.checked = normalizedSet.has(
                    String(fld.value ?? "").trim(),
                  );
                });
              });
            } else {
              // Single multi-checkbox group (non-repeater): value is array of all selected options
              const normalizedSet = this.normalizeValuesForChecks(value);
              inputs.forEach((fld) => {
                fld.checked = normalizedSet.has(String(fld.value ?? "").trim());
              });
            }
          } else if (fieldData.type === "radio") {
            const areaScope =
              useIndexed && inputs[0] && inputs[0].closest(".container-fluid");
            const rows = areaScope
              ? areaScope.querySelectorAll(".colsListWrapper")
              : [];
            values.forEach((rowVal, idx) => {
              let rowInputs = [];
              if (useIndexed && rows.length > idx) {
                const row = rows[idx];
                const formCol =
                  row && row.querySelector('.formCol[data-id="' + key + '"]');
                rowInputs = formCol
                  ? Array.from(formCol.querySelectorAll('input[type="radio"]'))
                  : [];
              }
              if (rowInputs.length === 0) {
                const rowInputName = useIndexed
                  ? `${key}[${idx}]`
                  : inputs[idx]?.name || key + "[]";
                rowInputs = Array.from(
                  scope.querySelectorAll(
                    `[name="${rowInputName.replace(/["\\\[\]]/g, "\\$&")}"]`,
                  ),
                );
              }
              if (rowInputs.length === 0) return;
              const valStr = rowVal != null ? String(rowVal).trim() : "";
              rowInputs.forEach((fld) => {
                const optionValue = String(fld.value ?? "").trim();
                const shouldCheck = optionValue === valStr;
                fld.checked = shouldCheck;
              });
            });
          } else {
            values.forEach((v, idx) => {
              const field = inputs[idx];
              if (!field) return;
              if (fieldData.type === "date" && field._flatpickr) {
                field._flatpickr.setDate(
                  DateUtils.toDisplayDate(v != null ? v : ""),
                  false,
                );
              } else {
                field.value = v != null ? v : "";
              }
            });
            inputs.forEach((field, idx) => {
              if (
                field.tagName === "SELECT" &&
                window.NiceSelect &&
                field.classList.contains("tqf_select")
              ) {
                setTimeout(() => this.updateNiceSelect(field), 100);
                setTimeout(
                  () =>
                    field.dispatchEvent(new Event("change", { bubbles: true })),
                  150,
                );
              }
            });
          }
        }
        return;
      }

      // Single value (non-repeater or single row)
      const field = scope.querySelector(
        `[name="${key}"], [name="${key}[]"], [name="${key}[0]"], [id="${key}"]`,
      );
      if (!field) return;

      // Israeli bank branch: resolve branch code to name via BOI XML (deferred until map is loaded)
      if (fieldData.inputFunction === "israeliBankBranch" && value != null) {
        const bankSelectorId = getBankSelectorIdForBranchField(
          this.formConfig,
          key,
        );
        const bankName = bankSelectorId
          ? (normalized[bankSelectorId] ??
            scope.querySelector(`select[name="${bankSelectorId}"]`)?.value ??
            "")
          : "";
        bankBranchPending.push({ key, value, bankName });
        return;
      }

      // Handle different field types
      if (fieldData.type === "file" && value) {
        const fileValues = Array.isArray(value) ? value : [value];
        let insertBefore = field.nextSibling;
        fileValues.forEach((fileRef, idx) => {
          if (fileRef == null || String(fileRef).trim() === "") return;
          const fileLink = document.createElement("a");
          fileLink.href = fileRef;
          fileLink.target = "_blank";
          fileLink.className = "file_refernce";
          fileLink.setAttribute("data-field_id", key);
          fileLink.textContent =
            fileValues.length > 1 ? `Open File ${idx + 1}` : "Open File";
          fileLink.style.display = "block";
          if (insertBefore) {
            field.parentNode.insertBefore(fileLink, insertBefore);
          } else {
            field.parentNode.appendChild(fileLink);
            insertBefore = fileLink.nextSibling;
          }
        });
        field.removeAttribute("required");
      } else if (fieldData.type === "signature" && value) {
        const img = document.createElement("img");
        img.src = value;
        img.style.display = "block";

        const signButton = scope.querySelector(`.sign_me[data-id="${key}"]`);
        if (signButton) {
          signButton.classList.add("d-none");
          if (signButton.nextSibling) {
            signButton.parentNode.insertBefore(img, signButton.nextSibling);
          } else {
            signButton.parentNode.appendChild(img);
          }
        }

        field.value = value;
      } else if (field.type === "checkbox" || field.type === "radio") {
        const fieldChecks = scope.querySelectorAll(
          `[name="${field.name}"], [name="${key}[]"]`,
        );
        const isRadio = fieldData.type === "radio";
        const normalizedValues = this.normalizeValuesForChecks(value, {
          splitCommas: !isRadio,
        });
        // #region agent log
        if (
          isRadio &&
          typeof value === "string" &&
          value.includes(",")
        ) {
          const matchedCount = Array.from(fieldChecks).filter((fld) =>
            normalizedValues.has(String(fld.value ?? "").trim()),
          ).length;
          fetch(
            "http://127.0.0.1:7732/ingest/8f4fd268-bbd7-467b-bf7e-fa28d0a32d47",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "X-Debug-Session-Id": "6b3963",
              },
              body: JSON.stringify({
                sessionId: "6b3963",
                location: "form_engine.js:populateFormData:radio",
                message: "scalar radio restore",
                data: {
                  fieldId: key,
                  valueLen: value.length,
                  normSetSize: normalizedValues.size,
                  optionCount: fieldChecks.length,
                  matchedCount,
                  runId: "post-fix",
                },
                timestamp: Date.now(),
                hypothesisId: "H1-comma-split",
              }),
            },
          ).catch(() => {});
        }
        // #endregion
        fieldChecks.forEach((fld) => {
          const optionValue = String(fld.value ?? "").trim();
          fld.checked = normalizedValues.has(optionValue);
        });
      } else if (field.tagName === "SELECT") {
        field.value = value || "";
        if (window.NiceSelect && field.classList.contains("tqf_select")) {
          setTimeout(() => this.updateNiceSelect(field), 100);
        }
        setTimeout(
          () => field.dispatchEvent(new Event("change", { bubbles: true })),
          150,
        );
      } else if (fieldData.type === "date" && field._flatpickr) {
        field._flatpickr.setDate(
          DateUtils.toDisplayDate(value != null ? value : ""),
          false,
        );
      } else {
        field.value = value != null ? value : "";
      }
    });

    // Resolve Israeli bank branch codes to names via BOI XML
    if (bankBranchPending.length > 0) {
      try {
        const boiUrl = this.container?.dataset?.boiBranchesUrl || undefined;
        const branchMap = await loadBranchMap(boiUrl ? { url: boiUrl } : {});
        bankBranchPending.forEach(({ key, value, bankName }) => {
          const fields = this.container.querySelectorAll(
            `[name="${key}"], [name="${key}[]"]`,
          );
          const branchValues = Array.isArray(value) ? value : [value];
          branchValues.forEach((branchCode, idx) => {
            const displayName = getBranchNameFromMap(
              branchMap,
              bankName,
              branchCode,
            );
            const el = fields[idx];
            if (el) el.value = displayName;
          });
        });
      } catch (e) {
        bankBranchPending.forEach(({ key, value }) => {
          const fields = this.container.querySelectorAll(
            `[name="${key}"], [name="${key}[]"]`,
          );
          const branchValues = Array.isArray(value) ? value : [value];
          branchValues.forEach((code, idx) => {
            if (fields[idx]) fields[idx].value = code;
          });
        });
      }
    }

    // Trigger conditions after populating
    this.triggerConditions();

    if (this.valueRulesEngine && this.formConfig?.field_value_rules?.length) {
      this.valueRulesEngine.evaluateValueRules();
    }

    FieldSyncHelper.runInitialSync();
    CustomersBasedFillHelper.runInitialSync();
  }

  /**
   * Render form
   */
  renderForm() {
    if (!this.formConfig) return;

    const sortedPages = FormConfig.sortPages(this.formConfig.pages);

    sortedPages.forEach((page) => {
      const pageNum = page.poisition || page.position || 1;
      const pageId = page.id;
      const extraClass = (page.className || page.class_name || "").trim();

      const pageElement = document.createElement("div");
      pageElement.className = `page ${pageNum === 1 ? "" : "d-none"}${extraClass ? " " + extraClass : ""}`;
      pageElement.setAttribute("data-num", pageNum);
      pageElement.setAttribute("data-id", pageId);

      const formSortable = document.createElement("div");
      formSortable.className = "formSortable";
      pageElement.appendChild(formSortable);

      const areas = FormConfig.getAreasForPage(this.formConfig, pageId);

      areas.forEach((area) => {
        this.renderArea(area, formSortable);
      });

      this.container.appendChild(pageElement);
    });

    this.replaceRepeaterDuplicateLinksWithButtons();

    this.setupCollapsibleToggleHandlers();

    // Multi-checkbox groups: never use HTML required (browser would require every box).
    this.stripRequiredFromCheckboxGroups();
    // Ensure form has novalidate so browser never blocks submit (we validate in JS)
    const form = this.getFormElement();
    if (form) {
      form.setAttribute("novalidate", "");
    }
  }

  /**
   * Form-level options passed to field renderer (e.g. date_input_format for Flatpickr).
   * @returns {{ date_input_format: string }}
   */
  getFormOptions() {
    return { date_input_format: this.formConfig?.date_input_format || "Y-m-d" };
  }

  /**
   * Initialize Flatpickr on all date inputs that have data-flatpickr="true".
   * @param {Element} [scope] - Optional scope to search within (e.g. a new repeater row). Defaults to this.container.
   */
  initDatePickers(scope = null) {
    const root = scope || this.container;
    if (!root) return;
    root.querySelectorAll('[data-flatpickr="true"]').forEach((el) => {
      if (el._flatpickr) return;
      const isReadonly = el.hasAttribute("readonly") || el.readOnly;
      flatpickr(el, {
        dateFormat: el.dataset.dateFormat || "Y-m-d",
        altInput: true,
        altFormat: el.dataset.altFormat || "Y-m-d",
        minDate: el.dataset.minDate || undefined,
        maxDate: el.dataset.maxDate || undefined,
        defaultDate: el.dataset.defaultDate || undefined,
        allowInput: !isReadonly,
        clickOpens: !isReadonly,
      });
    });
  }

  /**
   * Replace repeater duplicate <a href="#"> with <button> so the whole control is clickable and never scrolls.
   */
  replaceRepeaterDuplicateLinksWithButtons() {
    if (!this.container) return;
    this.container
      .querySelectorAll('.repeater a.duplicate[href="#"]')
      .forEach((link) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = link.className;
        btn.innerHTML = link.innerHTML;
        link.parentNode.replaceChild(btn, link);
      });
  }

  /**
   * Remove required attribute from all checkbox inputs in multi-checkbox groups (name ends with []).
   * Required for those groups is enforced in JS as "at least one checked" only.
   */
  stripRequiredFromCheckboxGroups() {
    const form = this.getFormElement();
    if (!form) return;
    const checkboxes = form.querySelectorAll('input[type="checkbox"][name]');
    checkboxes.forEach((input) => {
      if (input.name && input.name.endsWith("[]")) {
        input.removeAttribute("required");
        input.removeAttribute("data-required");
      }
    });
  }

  /**
   * Render area
   * @param {Object} area - Area configuration
   * @param {Element} container - Container element
   */
  /**
   * Default area template when the page does not provide .areaTemplate (e.g. public form view).
   * Must match formbuilder structure: container-fluid, ul.row.areaRow.colsListWrapper, and .repeater .duplicate for add-row.
   */
  static getDefaultAreaTemplate() {
    return (
      '<div class="container-fluid">' +
      '<ul class="row areaRow colsListWrapper" data-repeater="[R]" data-conditions=\'[C]\' data-visible="[V]" data-users-limit="[U]" data-toc="[T]" data-collapsible="[COLL]">[COLS]</ul>' +
      '<div class="repeater"><a href="#" class="duplicate">+ Add row</a></div>' +
      "</div>"
    );
  }

  renderArea(area, container) {
    const areaTemplateEl = document.querySelector(".areaTemplate");
    const areaTemplate = areaTemplateEl
      ? areaTemplateEl.innerHTML
      : FormEngine.getDefaultAreaTemplate();
    const cols = area.fields
      .map((fieldId) => {
        const field = FormConfig.getFieldById(this.formConfig, fieldId);
        if (!field) return "";

        const colSize = field.size || 12;
        return `<div class="col-md-${colSize} formCol" data-id="${field.id}" data-type="${field.type}"></div>`;
      })
      .join("");

    const repeaterVal = area.repeater || "0";
    const tocVal = FieldRenderer.replacePlaceholdersInString(
      area.toc || "",
      this.activeCustomer,
      this.behalfContext,
    );
    const visibleVal = area.visibleSettings || "a";
    const usersLimitJson = JSON.stringify(area.usersLimit || []);
    const conditionsJson = JSON.stringify(area.conditions || []);
    const collapsibleVal = area.collapsible ? "1" : "0";

    let areaHTML = areaTemplate
      .replace("[COLS]", cols)
      .replace("[R]", repeaterVal)
      .replace("[T]", tocVal)
      .replace("[V]", visibleVal)
      .replace('data-users-limit="[U]"', `data-users-limit='${usersLimitJson}'`)
      .replace("data-users-limit='[U]'", `data-users-limit='${usersLimitJson}'`)
      .replace('data-conditions="[C]"', `data-conditions='${conditionsJson}'`)
      .replace("data-conditions='[C]'", `data-conditions='${conditionsJson}'`)
      .replace(
        'data-collapsible="[COLL]"',
        `data-collapsible="${collapsibleVal}"`,
      );
    const visibleSetting = String(area.visibleSettings || "");
    let dbl = "d-block";
    if (visibleSetting === "c" || visibleSetting === "n") {
      dbl = "d-none";
    } else if (visibleSetting === "u") {
      const user = this.currentViewerUser ?? this.activeCustomer;
      const isLoggedIn =
        user != null &&
        typeof user === "object" &&
        Object.keys(user).length > 0;
      // console.log('[FormEngine] renderArea visibleSettings=u', { areaId: area.id, hasUser: !!user, isLoggedIn, userKeys: user ? Object.keys(user) : [], usersLimit: area.usersLimit });
      if (!isLoggedIn) {
        dbl = "d-none";
        // console.log('[FormEngine] area hidden (not logged in)', area.id);
      } else if (area.usersLimit && area.usersLimit.length > 0) {
        area.usersLimit.forEach((limit) => {
          const val = String(user[limit.value] ?? "").toLowerCase();
          const input = String(limit.input || "").toLowerCase();
          const match = val === input;
          // console.log('[FormEngine] usersLimit check', { areaId: area.id, fieldId: limit.value, userValue: user[limit.value], val, input, match });
        });
        const matches = area.usersLimit.some(
          (limit) =>
            String(user[limit.value] ?? "").toLowerCase() ===
            String(limit.input || "").toLowerCase(),
        );
        if (!matches) {
          dbl = "d-none";
        }
      }
    }

    const areaElement = document.createElement("div");
    areaElement.className = `${dbl} area`;
    areaElement.setAttribute("data-id", area.id);
    const repeaterValue = area.repeater || "0";
    areaElement.setAttribute("data-repeater", repeaterValue);
    areaElement.setAttribute("data-collapsible", area.collapsible ? "1" : "0");
    areaElement.innerHTML = areaHTML;

    container.appendChild(areaElement);

    // Hide repeater button if area is not a repeater (repeater === '0' or 0)
    const isRepeater =
      repeaterValue !== "0" && repeaterValue !== 0 && repeaterValue !== "";
    if (!isRepeater) {
      // Hide all repeater containers and duplicate buttons
      const repeaterContainers = areaElement.querySelectorAll(".repeater");
      repeaterContainers.forEach((container) => {
        container.style.display = "none";
      });

      // Also hide any standalone duplicate buttons that aren't in a repeater container
      const duplicateButtons = areaElement.querySelectorAll(".duplicate");
      duplicateButtons.forEach((button) => {
        if (!button.closest(".repeater")) {
          button.style.display = "none";
        }
      });
    }

    // Set translator on FieldRenderer before rendering fields
    FieldRenderer.setTranslator(this.translator);

    // Render fields in area - pass areaElement as context for scoped querySelector
    area.fields.forEach((fieldId) => {
      const field = FormConfig.getFieldById(this.formConfig, fieldId);
      if (field) {
        this.renderField(field, areaElement);
      }
    });

    // Repeater areas: rename original row fields to indexed names (field_id -> field_id[0])
    if (isRepeater) {
      const firstRow = areaElement.querySelector(".colsListWrapper");
      if (firstRow) {
        firstRow
          .querySelectorAll("input, textarea, select")
          .forEach((field) => {
            if (field.name && !field.name.includes("[")) {
              field.name = `${field.name}[0]`;
            }
          });
      }
      // Make radio/checkbox ids (and label[for]) unique per row so label clicks affect the correct row
      const rows = areaElement.querySelectorAll(".colsListWrapper");
      rows.forEach((row, rowIndex) => {
        this.ensureRepeaterRowUniqueIds(row, rowIndex);
      });
      // Collapsible: wrap each repeater row with header and toggle
      if (area.collapsible) {
        this.setupCollapsibleArea(areaElement, area);
      }
    }

    // Collapsible for non-repeater areas: wrap the single section so it can be
    // toggled too (repeater areas are already handled inside the block above).
    if (area.collapsible && !isRepeater) {
      this.setupCollapsibleArea(areaElement, area);
    }
  }

  /**
   * Build the label for a collapsible section.
   * Uses area.collapsibleTitle when set: as a full name for non-repeater areas,
   * or as a prefix followed by the row number for repeater areas.
   * Falls back to the default "Collapsible Section N".
   * @param {Object} area - Area configuration
   * @param {number} index - Zero-based section/row index
   * @returns {string}
   */
  collapsibleSectionLabel(area, index) {
    const rowNum = index + 1;
    const repeaterVal = area && area.repeater;
    const isRepeater =
      repeaterVal !== "0" && repeaterVal !== 0 && repeaterVal !== "" && repeaterVal != null;
    const custom =
      area && typeof area.collapsibleTitle === "string"
        ? area.collapsibleTitle.trim()
        : "";
    if (custom) {
      return isRepeater ? `${custom} ${rowNum}` : custom;
    }
    return `Collapsible Section ${rowNum}`;
  }

  /**
   * Wrap each .colsListWrapper in a collapsible row (header with toggle + content).
   * @param {Element} areaElement - The area DOM element
   * @param {Object} [area] - Area configuration (for section title)
   */
  setupCollapsibleArea(areaElement, area = null) {
    const wrappers = areaElement.querySelectorAll(".colsListWrapper");
    wrappers.forEach((colsListWrapper, index) => {
      const parent = colsListWrapper.parentNode;
      if (!parent) return;

      const rowWrapper = document.createElement("div");
      rowWrapper.className = "repeater-row-wrapper";

      const header = document.createElement("div");
      header.className = "area-row-header";
      header.setAttribute("role", "button");
      header.setAttribute("tabindex", "0");
      header.setAttribute("aria-expanded", "true");
      const toggleIcon = document.createElement("span");
      toggleIcon.className = "collapse-toggle-icon";
      toggleIcon.setAttribute("aria-hidden", "true");
      toggleIcon.textContent = "\u25BC"; // ▼ expanded (down triangle)
      const label = document.createElement("span");
      label.className = "area-row-label";
      label.textContent = this.collapsibleSectionLabel(area, index);
      header.appendChild(toggleIcon);
      header.appendChild(label);

      const contentWrap = document.createElement("div");
      contentWrap.className = "collapsible-content";

      parent.insertBefore(rowWrapper, colsListWrapper);
      contentWrap.appendChild(colsListWrapper);
      rowWrapper.appendChild(header);
      rowWrapper.appendChild(contentWrap);
    });
  }

  /**
   * Setup event delegation for collapse/expand toggles on repeater rows.
   */
  setupCollapsibleToggleHandlers() {
    if (!this.container) return;
    this.container.addEventListener("click", (e) => {
      const header = e.target.closest(".area-row-header");
      if (!header) return;
      e.preventDefault();
      const rowWrapper = header.closest(".repeater-row-wrapper");
      if (!rowWrapper) return;
      const content = rowWrapper.querySelector(".collapsible-content");
      const icon = rowWrapper.querySelector(".collapse-toggle-icon");
      const isCollapsed = rowWrapper.classList.toggle("collapsed");
      if (content) content.style.display = isCollapsed ? "none" : "";
      if (icon) {
        icon.textContent = isCollapsed ? "\u25B6" : "\u25BC"; // ▶ collapsed, ▼ expanded
      }
      header.setAttribute("aria-expanded", String(!isCollapsed));
    });
  }

  /**
   * Make input ids (and label[for]) unique within a repeater row so label clicks affect the correct row.
   * Radios/checkboxes use id="chk{fieldId}{optionIndex}" which is duplicated in every row; label[for] then
   * targets the first match in the document. This re-ids to chk{fieldId}{optionIndex}_r{rowIndex} per row.
   * @param {Element} row - Repeater row element (.colsListWrapper or container that holds the inputs)
   * @param {number} rowIndex - Row index (0, 1, 2, ...)
   */
  ensureRepeaterRowUniqueIds(row, rowIndex) {
    if (!row || typeof rowIndex !== "number") return;
    row.querySelectorAll("input, select, textarea").forEach((field) => {
      const oldId = field.id;
      if (!oldId) return;
      const newId = oldId + "_r" + rowIndex;
      const escapedOldId = CSS.escape(oldId);
      row.querySelectorAll(`label[for="${escapedOldId}"]`).forEach((label) => {
        label.htmlFor = newId;
      });
      field.id = newId;
    });
  }

  /**
   * Render field
   * @param {Object} fieldConfig - Field configuration
   * @param {Element} container - Optional container element for scoped search
   */
  renderField(fieldConfig, container = null) {
    try {
      // Use scoped search if container provided, otherwise fall back to document search
      const searchScope = container || document;
      const fieldContainer = searchScope.querySelector(
        `[data-id="${fieldConfig.id}"]`,
      );

      if (!fieldContainer) {
        return;
      }

      const html = FieldRenderer.render(
        fieldConfig,
        this.activeCustomer,
        this.behalfContext,
        this.formSourceContext,
        this.getFormOptions(),
      );

      if (!html || html.trim() === "") {
        return;
      }

      fieldContainer.innerHTML = html;
      fieldContainer.setAttribute("data-type", fieldConfig.type);
    } catch (error) {}
  }

  /**
   * Render one empty repeater row (no cloning). Used when adding a new repeater row so the new row has empty fields.
   * @param {Object} area - Area configuration from formConfig
   * @param {number} newRowIndex - Index for field names (e.g. field_id[newRowIndex])
   * @returns {Element|null} Wrapper element (colsListWrapper) with delete button and row, or null
   */
  renderEmptyRepeaterRow(area, newRowIndex) {
    if (!this.formConfig || !area || !Array.isArray(area.fields)) return null;

    const cols = area.fields
      .map((fieldId) => {
        const field = FormConfig.getFieldById(this.formConfig, fieldId);
        if (!field) return "";
        const colSize = field.size || 12;
        return `<div class="col-md-${colSize} formCol" data-id="${field.id}" data-type="${field.type}"></div>`;
      })
      .join("");

    const rowWrap = document.createElement("div");
    rowWrap.innerHTML = `<div class="row">${cols}</div>`;
    const row = rowWrap.firstElementChild;
    if (!row) return null;

    FieldRenderer.setTranslator(this.translator);
    area.fields.forEach((fieldId) => {
      const field = FormConfig.getFieldById(this.formConfig, fieldId);
      if (!field) return;
      const html = FieldRenderer.render(
        field,
        {},
        null,
        null,
        this.getFormOptions(),
      );
      if (!html || !html.trim()) return;
      const col = row.querySelector(`.formCol[data-id="${field.id}"]`);
      if (col) {
        col.innerHTML = html;
        col.setAttribute("data-type", field.type);
      }
    });

    row.querySelectorAll("input, textarea, select").forEach((field) => {
      if (field.name) {
        const baseName = field.name.replace(/\[\d+\]$/, "");
        field.name = `${baseName}[${newRowIndex}]`;
      }
    });

    row.querySelectorAll("select").forEach((select) => {
      select.classList.add("bind_nice_select");
    });

    const wrapper = document.createElement("div");
    wrapper.className = "row areaRow colsListWrapper";
    const deleteButton = document.createElement("a");
    deleteButton.href = "#";
    deleteButton.className = "btn btn-danger delete_repeater";
    deleteButton.textContent = "X";
    wrapper.appendChild(deleteButton);
    wrapper.appendChild(row);

    this.ensureRepeaterRowUniqueIds(wrapper, newRowIndex);

    if (area.collapsible) {
      const rowWrapper = document.createElement("div");
      rowWrapper.className = "repeater-row-wrapper";
      const header = document.createElement("div");
      header.className = "area-row-header";
      header.setAttribute("role", "button");
      header.setAttribute("tabindex", "0");
      header.setAttribute("aria-expanded", "true");
      const toggleIcon = document.createElement("span");
      toggleIcon.className = "collapse-toggle-icon";
      toggleIcon.setAttribute("aria-hidden", "true");
      toggleIcon.textContent = "\u25BC"; // ▼ expanded (down triangle)
      const label = document.createElement("span");
      label.className = "area-row-label";
      label.textContent = this.collapsibleSectionLabel(area, newRowIndex);
      header.appendChild(toggleIcon);
      header.appendChild(label);
      const contentWrap = document.createElement("div");
      contentWrap.className = "collapsible-content";
      contentWrap.appendChild(wrapper);
      rowWrapper.appendChild(header);
      rowWrapper.appendChild(contentWrap);
      return rowWrapper;
    }
    return wrapper;
  }

  /**
   * Setup conditions
   */
  setupConditions() {
    if (!this.formConfig) return;

    // Setup area conditions
    this.formConfig.areas.forEach((area) => {
      if (
        area.visibleSettings === "c" &&
        area.conditions &&
        area.conditions.length > 0
      ) {
        this.conditionEngine.setupAreaConditions(area.id, area.conditions);
      }
    });

    // Setup field conditions (from top-level fields)
    this.formConfig.fields.forEach((field) => {
      if (
        field.visibleSettings === "c" &&
        field.conditions &&
        field.conditions.length > 0
      ) {
        this.conditionEngine.setupFieldConditions(field.id, field.conditions);
      }
    });

    // Also register from areas so every field that appears in an area and has conditions is in the delegation map
    this.formConfig.areas.forEach((area) => {
      if (area.fields && Array.isArray(area.fields)) {
        area.fields.forEach((fieldId) => {
          const field = FormConfig.getFieldById(this.formConfig, fieldId);
          if (
            field &&
            field.visibleSettings === "c" &&
            field.conditions &&
            field.conditions.length > 0
          ) {
            this.conditionEngine.setupFieldConditions(
              field.id,
              field.conditions,
            );
          }
        });
      }
    });
  }

  /**
   * Trigger conditions (after data load)
   */
  triggerConditions() {
    const allFields = document.querySelectorAll("input, select, textarea");
    allFields.forEach((field) => {
      field.dispatchEvent(new Event("input", { bubbles: true }));
      field.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  /**
   * Setup pages
   */
  setupPages() {
    if (!this.formConfig || !this.formConfig.pages) return;

    this.pageManager = new PageManager(this.validator, ErrorManager);
    this.pageManager.initialize(this.formConfig.pages);
    this.pageManager.onPageChange = () => {
      this.applyOnePageAreaImage();
      try {
        this.updateNextButtonStateForSignatures();
      } catch (err) {}
    };

    this.pageNavigation = new PageNavigation(this.pageManager, this.translator);
    this.pageNavigation.setupNavigationButtons();
    this.pageNavigation.setupBrandingNavigation(this.formConfig.pages);

    setTimeout(() => this.updateNextButtonStateForSignatures(), 0);
  }

  /**
   * Setup field handlers
   */
  setupFieldHandlers() {
    // Setup phone inputs
    this.setupPhoneInputs();

    // Setup nice-select
    this.setupNiceSelect();

    // Setup signature pads
    this.setupSignaturePads();

    // Setup calculated fields
    this.setupCalculatedFields();

    // Setup shadow fields
    this.setupShadowFields();

    // Setup field sync (syncFields)
    this.setupFieldSync();

    // Fill fields from customers_based selection
    this.setupCustomersBasedFill();

    // Setup validation listeners
    this.setupValidationListeners();

    // Setup user edit limits
    this.setupUserEditLimits();

    // Setup blocker options
    this.setupBlockerOptions();

    // Setup alert options
    this.setupAlertOptions();

    // Israeli bank/branch: defer so DOM and NiceSelect are fully ready, then wire bank select and branch dropdowns
    setTimeout(() => {
      setupIsraeliBankSelector(this.container, this.formConfig, {
        url: this.container?.dataset?.boiBranchesUrl || undefined,
        onNiceSelectUpdate: (sel) => this.updateNiceSelect(sel),
      });
    }, 0);
  }

  /**
   * Setup phone inputs
   */
  setupPhoneInputs() {
    IntlTelInputWrapper.init();
  }

  /**
   * Setup nice-select
   */
  setupNiceSelect() {
    if (!window.NiceSelect) return;

    const selects = document.querySelectorAll(".tqf_select:not([disabled])");
    selects.forEach((select) => {
      if (select.id) {
        window.NiceSelect.bind(select);
      } else {
        const options = {};
        if (select.dataset.searchable) {
          options.searchable = true;
          options.searchPlaceholder = "Search";
        }
        window.NiceSelect.bind(select, options);
      }
    });

    setTimeout(() => {
      ErrorManager.repositionSelectErrorContainers();
    }, 100);

    document.addEventListener(
      "click",
      (e) => {
        if (e.target.closest(".nice-select-dropdown .list .option")) {
          const div = e.target.closest(".form-group");
          const select = div?.querySelector("select");
          if (select) {
            // This runs in the capture phase, before NiceSelect updates select.value.
            // Defer so listeners (the condition engine reads value synchronously) see
            // the new value. Mark as a real user selection so the condition engine
            // (which otherwise drops untrusted events) re-evaluates field conditions.
            setTimeout(() => {
              const changeEvt = new Event("change", { bubbles: true });
              const inputEvt = new Event("input", { bubbles: true });
              changeEvt.tqUserSelect = true;
              inputEvt.tqUserSelect = true;
              select.dispatchEvent(changeEvt);
              select.dispatchEvent(inputEvt);
            }, 0);
          }
        }
      },
      true,
    );
  }

  /**
   * Update nice-select UI for a given select element
   * @param {HTMLSelectElement} selectElement - The select element to update
   */
  updateNiceSelect(selectElement) {
    if (
      !window.NiceSelect ||
      !selectElement ||
      !selectElement.classList.contains("tqf_select")
    ) {
      return;
    }

    // Find the nice-select wrapper div
    const niceSelectDiv =
      selectElement.parentNode?.querySelector(".nice-select");
    if (!niceSelectDiv) {
      return; // Nice-select not initialized yet, skip
    }

    // Get the current value and find the matching option
    const currentValue = selectElement.value;
    let selectedOption = null;
    let displayText = "";

    // Find option by value (more reliable than selectedIndex)
    if (
      currentValue !== null &&
      currentValue !== undefined &&
      currentValue !== ""
    ) {
      selectedOption = Array.from(selectElement.options).find(
        (opt) => opt.value === currentValue,
      );
    }

    // If no option found by value, try selectedIndex
    if (!selectedOption && selectElement.selectedIndex >= 0) {
      selectedOption = selectElement.options[selectElement.selectedIndex];
    }

    // Get display text from the selected option
    if (selectedOption) {
      displayText =
        selectedOption.textContent ||
        selectedOption.innerText ||
        selectedOption.value ||
        "";
    } else {
      // No selection - use placeholder or empty
      displayText = selectElement.options[0]?.textContent || "";
    }

    // Try to get the nice-select instance from various possible locations
    let niceSelectInstance = selectElement.niceSelect;
    if (!niceSelectInstance) {
      niceSelectInstance =
        niceSelectDiv.niceSelectInstance || niceSelectDiv._niceSelectInstance;
    }

    // Also try to get it from the NiceSelect's internal registry if available
    if (!niceSelectInstance && window.NiceSelect.data) {
      niceSelectInstance = window.NiceSelect.data(selectElement);
    }

    // If instance found and has update method, use it
    if (niceSelectInstance && typeof niceSelectInstance.update === "function") {
      try {
        niceSelectInstance.update();
        // Also manually update display text to ensure it's correct
        const currentText = niceSelectDiv.querySelector(".current");
        if (currentText && displayText) {
          currentText.textContent = displayText;
        }
        return;
      } catch (e) {}
    }

    // Fallback: Manually update the display text
    const currentText = niceSelectDiv.querySelector(".current");
    if (currentText && displayText) {
      currentText.textContent = displayText;
    }

    // Update selected state in dropdown options
    const dropdownOptions = niceSelectDiv.querySelectorAll(".option");
    dropdownOptions.forEach((option, index) => {
      const optionValue = option.getAttribute("data-value");
      const isSelected =
        optionValue === currentValue ||
        (selectedOption && index === selectElement.selectedIndex);

      if (isSelected) {
        option.classList.add("selected");
        option.classList.add("focus");
      } else {
        option.classList.remove("selected");
        option.classList.remove("focus");
      }
    });

    // Trigger events that nice-select might listen to
    const inputEvent = new Event("input", { bubbles: true, cancelable: true });
    selectElement.dispatchEvent(inputEvent);
  }

  /**
   * Setup signature pads
   */
  setupSignaturePads() {
    SignaturePadWrapper.setTranslator(this.translator);
    SignaturePadWrapper.init(this.container);

    document.body.addEventListener("tqtag-signature-completed", () => {
      try {
        this.updateNextButtonStateForSignatures();
      } catch (err) {}
    });
  }

  /**
   * Setup calculated fields
   */
  setupCalculatedFields() {
    if (!this.formConfig) return;

    this.formConfig.fields.forEach((field) => {
      if (
        field.type === "calculated" &&
        field.calculation &&
        field.calculation.length > 0
      ) {
        CalculatedFieldHelper.setup(
          field.id,
          field.calculation,
          field.decimalPoints || 0,
        );
      }
    });
  }

  /**
   * Setup shadow fields
   */
  setupShadowFields() {
    if (!this.formConfig) return;

    this.formConfig.fields.forEach((field) => {
      if (
        field.type === "shadow" &&
        field.syncFields &&
        field.syncFields.length > 0
      ) {
        ShadowFieldHelper.setup(field.id, field.syncFields, this.getFormOptions());
      }
    });
  }

  /**
   * Setup field sync (syncFields) for regular fields
   * Config is target-centric: field.syncFields lists source IDs to copy from into this field.
   * We build sourceId -> [targetIds] and register each source so when it changes we copy to all targets.
   */
  setupFieldSync() {
    if (!this.formConfig) return;

    const sourceToTargets = new Map();

    this.formConfig.fields.forEach((field) => {
      if (field.type === "shadow") return;
      if (
        !field.syncFields ||
        !Array.isArray(field.syncFields) ||
        field.syncFields.length === 0
      )
        return;

      field.syncFields.forEach((sourceId) => {
        if (!sourceId) return;
        let targets = sourceToTargets.get(sourceId);
        if (!targets) {
          targets = [];
          sourceToTargets.set(sourceId, targets);
        }
        if (!targets.includes(field.id)) {
          targets.push(field.id);
        }
      });
    });

    sourceToTargets.forEach((targetIds, sourceId) => {
      FieldSyncHelper.setup(sourceId, targetIds);
    });

    FieldSyncHelper.runInitialSync();
  }

  /**
   * Fill target fields when a customers_based dropdown selection changes.
   */
  setupCustomersBasedFill() {
    CustomersBasedFillHelper.setup(this.formConfig);
  }

  /**
   * Setup validation listeners
   */
  setupValidationListeners() {
    // Validate on blur (after user leaves field)
    document.addEventListener(
      "blur",
      (e) => {
        if (e.target.matches("input, select, textarea")) {
          const fieldId = e.target.name || e.target.id;
          this.formState.touchField(fieldId);

          // Add touched class for CSS styling
          e.target.classList.add("touched");

          // Validate after field is touched
          setTimeout(() => {
            const rawId =
              fieldId && fieldId.endsWith("[]")
                ? fieldId.slice(0, -2)
                : fieldId;
            const fieldConfig =
              FormConfig.getFieldById(this.formConfig, rawId) || {};
            this.validator.validateField(e.target, fieldConfig, true);
          }, 100);
        }
      },
      true,
    );

    // Clear errors on input if field was previously validated
    // Skip Israeli ID/phone fields - they have their own validation
    document.addEventListener(
      "input",
      (e) => {
        if (e.target.matches("input, select, textarea")) {
          const field = e.target;
          const inputFunction = field.getAttribute("data-input-function");
          // Skip Israeli validations - they handle their own errors
          if (
            inputFunction === "israeliIDNumber" ||
            inputFunction === "israeliPhoneNumber"
          ) {
            return;
          }

          const fieldId = field.name || field.id;
          this.formState.updateFieldValue(fieldId, field.value);
          // Only clear error if field was previously validated (touched)
          if (this.formState.isFieldTouched(fieldId)) {
            ErrorManager.clearError(field);
          }
        }
      },
      true,
    );
  }

  /**
   * Setup Israeli ID and phone number validations
   * Uses event delegation to handle dynamically added fields
   */
  setupIsraeliValidations() {
    // Use event delegation on document to catch all fields, including dynamically added ones
    document.addEventListener(
      "input",
      (e) => {
        const field = e.target;
        if (
          !field ||
          !(
            field instanceof HTMLInputElement ||
            field instanceof HTMLTextAreaElement
          )
        ) {
          return;
        }

        // Check for Israeli ID number validation
        if (
          field.hasAttribute("data-input-function") &&
          field.getAttribute("data-input-function") === "israeliIDNumber"
        ) {
          const idNumber = field.value;
          const fieldId = field.name || field.id;

          if (!idNumber || !idNumber.trim()) {
            // Clear error if empty (required validation will handle it)
            if (this.formState.isFieldTouched(fieldId)) {
              ErrorManager.clearError(field);
            }
            return;
          }

          const isValid = PhoneUtils.validateIsraeliID(idNumber);
          const rawId =
            fieldId && fieldId.endsWith("[]") ? fieldId.slice(0, -2) : fieldId;
          const fieldConfig =
            FormConfig.getFieldById(this.formConfig, rawId) || {};

          if (isValid) {
            ErrorManager.clearError(field);
            // Also validate with the validator to update form state
            if (this.formState.isFieldTouched(fieldId)) {
              this.validator.validateField(field, fieldConfig, true);
            }
          } else {
            const errorMessage = this.translator.t("errors.enter_valid_id");
            ErrorManager.showError(field, errorMessage);
            this.formState.setValidationState(fieldId, false, errorMessage);
          }
          return;
        }

        // Check for Israeli phone number validation
        if (
          field.hasAttribute("data-input-function") &&
          field.getAttribute("data-input-function") === "israeliPhoneNumber"
        ) {
          const phone = field.value;
          const fieldId = field.name || field.id;

          if (!phone || !phone.trim()) {
            // Clear error if empty (required validation will handle it)
            if (this.formState.isFieldTouched(fieldId)) {
              ErrorManager.clearError(field);
            }
            return;
          }

          const isValid = PhoneUtils.validateIsraeliPhone(phone);
          const rawId =
            fieldId && fieldId.endsWith("[]") ? fieldId.slice(0, -2) : fieldId;
          const fieldConfig =
            FormConfig.getFieldById(this.formConfig, rawId) || {};

          if (isValid) {
            ErrorManager.clearError(field);
            // Also validate with the validator to update form state
            if (this.formState.isFieldTouched(fieldId)) {
              this.validator.validateField(field, fieldConfig, true);
            }
          } else {
            const errorMessage = this.translator.t("errors.enter_valid_phone");
            ErrorManager.showError(field, errorMessage);
            this.formState.setValidationState(fieldId, false, errorMessage);
          }
          return;
        }
      },
      true,
    );
  }

  /**
   * Setup user edit limits
   */
  setupUserEditLimits() {
    const user = this.currentViewerUser ?? this.activeCustomer;
    if (!this.formConfig || !user) return;

    this.formConfig.fields.forEach((field) => {
      if (field.usersEditLimit && field.usersEditLimit.length > 0) {
        const formElement = document.querySelector(`[name="${field.id}"]`);
        if (!formElement) return;

        const isEditable = field.usersEditLimit.some(
          (limit) => user[limit.value] === limit.input,
        );

        if (!isEditable) {
          formElement.removeAttribute("required");
          if (
            formElement.tagName === "INPUT" ||
            formElement.tagName === "TEXTAREA"
          ) {
            formElement.readOnly = true;
          } else {
            formElement.disabled = true;
          }
        }
      }
    });
  }

  /**
   * Setup blocker options
   */
  setupBlockerOptions() {
    document.addEventListener(
      "change",
      (e) => {
        if (e.target.tagName === "SELECT") {
          this.handleBlockerOptions();
        }
      },
      true,
    );

    this.handleBlockerOptions();
  }

  /**
   * Handle blocker options
   */
  handleBlockerOptions() {
    const selects = document.querySelectorAll("select");
    let hasBlocker = false;

    selects.forEach((select) => {
      if (select.selectedIndex > 0) {
        const selectedOption = select.options[select.selectedIndex];
        if (
          selectedOption &&
          selectedOption.getAttribute("data-blocker") === "1"
        ) {
          hasBlocker = true;
        }
      }
    });

    const submitHolders = document.querySelectorAll(".submit_holder");
    submitHolders.forEach((holder) => {
      if (hasBlocker) {
        holder.classList.add("d-none");
      } else {
        holder.classList.remove("d-none");
      }
    });
  }

  /**
   * Setup alert options
   */
  setupAlertOptions() {
    document.addEventListener(
      "change",
      (e) => {
        if (e.target.tagName !== "SELECT") return;
        const select = e.target;
        // Defer so we read the value after the browser (and NiceSelect) have updated the select
        setTimeout(() => {
          const selectedValue = select.value;
          if (
            selectedValue === null ||
            selectedValue === undefined ||
            selectedValue === ""
          )
            return;
          const selectedOption = Array.from(select.options).find(
            (opt) => opt.value === selectedValue,
          );
          if (!selectedOption || !selectedOption.hasAttribute("data-alert"))
            return;
          const alertMessage = selectedOption.getAttribute("data-alert");
          if (
            !alertMessage ||
            typeof alertMessage !== "string" ||
            alertMessage.trim() === "" ||
            this.formState.isEditMode()
          )
            return;
          if (window.Swal) {
            window.Swal.fire({
              html: alertMessage,
              didOpen: () => {
                const hideAlertInputs = () => {
                  const popup = document.querySelector(".swal2-popup");
                  if (!popup) return;
                  const selector =
                    ".swal2-select, .swal2-input, .swal2-file, .swal2-range, .swal2-radio, .swal2-checkbox, .swal2-textarea";
                  popup.querySelectorAll(selector).forEach((el) => {
                    el.remove();
                  });
                  popup.querySelectorAll(selector).forEach((el) => {
                    el.style.setProperty("display", "none", "important");
                  });
                };
                hideAlertInputs();
                setTimeout(hideAlertInputs, 0);
              },
            });
          } else {
            alert(alertMessage);
          }
        }, 0);
      },
      true,
    );
  }

  /**
   * Resolve the form element (may be container, container's parent, or #tqform)
   */
  getFormElement() {
    if (this.container?.tagName === "FORM") return this.container;
    const byId = document.getElementById("tqform");
    if (byId) return byId;
    if (this.container?.closest) return this.container.closest("form");
    return this.container?.querySelector?.("form") || null;
  }

  /**
   * Setup form submission
   */
  setupFormSubmission() {
    const form = this.getFormElement();
    if (!form) return;

    // Prevent browser constraint validation so we control validation in JS (e.g. "at least one" for checkboxes)
    form.setAttribute("novalidate", "");

    this.formSubmitter = new FormSubmitter(
      this.formId,
      this.formState,
      this.validator,
      form,
      this.formConfig,
    );

    form.addEventListener("submit", async (e) => {
      e.preventDefault();

      // Mark all fields as touched before validation (exclude elements inside Swal popups)
      const allFields = form.querySelectorAll("input, select, textarea");
      allFields.forEach((field) => {
        if (field.closest && field.closest(".swal2-popup")) return;
        field.classList.add("touched");
        const fieldId = field.name || field.id;
        if (fieldId) {
          this.formState.touchField(fieldId);
        }
      });

      // Validate all fields before submission
      if (!this.validator.validateAll()) {
        LoadingSpinner.hideSubmission();
        // Focus on first invalid field
        const firstError = form.querySelector(
          ".error-container.validation-error-message:not(:empty)",
        );
        if (firstError) {
          const fieldId = firstError.getAttribute("data-field-id");
          let field = form.querySelector(
            `[name="${fieldId}"], [id="${fieldId}"]`,
          );
          if (
            !field &&
            fieldId &&
            document.getElementsByName(fieldId).length > 0
          ) {
            field = document.getElementsByName(fieldId)[0];
          }
          if (field) {
            field.scrollIntoView({ behavior: "smooth", block: "center" });
            field.focus();
          }
        }
        return;
      }

      try {
        // Prevent race where autosave draft fires while final submit is in progress.
        if (typeof this.draftAutoSaveCleanup === "function") {
          this.draftAutoSaveCleanup();
        }
        LoadingSpinner.showSubmission();

        const result = await this.formSubmitter.submit({
          currentUser: this.activeCustomer,
          submissionLimits: this.formConfig.submission_limits || {},
          onProgress: (stage) => {
            // Progress callback
          },
          setHiddenFieldDefaults: () => {
            this.setHiddenFieldDefaults();
          },
        });

        if (result.status === "ok") {
          try {
            await this.syncCartLineItems();
          } catch (cartError) {}
          // Check for redirect URL in formConfig.redirect first
          let redirectUrl = this.formConfig.redirect;

          // If no redirect in formConfig, check for thankYou in branding with type "redirect"
          if (
            !redirectUrl &&
            this.formConfig.branding &&
            this.formConfig.branding.thankYou
          ) {
            const thankYou = this.formConfig.branding.thankYou;
            if (thankYou.type === "redirect" && thankYou.url) {
              redirectUrl = thankYou.url;
            }
          }

          if (redirectUrl) {
            // Redirect immediately without showing SweetAlert
            window.location.href = redirectUrl;
            return; // Exit early to prevent any other code from running
          } else {
            // Check for thank you message configuration
            let thankYouMessage = "Form submitted successfully";
            if (this.formConfig.branding && this.formConfig.branding.thankYou) {
              const thankYou = this.formConfig.branding.thankYou;
              if (thankYou.type === "message" && thankYou.message) {
                thankYouMessage = thankYou.message;
              }
            }

            if (window.Swal) {
              window.Swal.fire({
                icon: "success",
                title: thankYouMessage,
                didOpen: () => {
                  const hideThankYouInputs = () => {
                    const popup = document.querySelector(".swal2-popup");
                    if (!popup) return;
                    const selector =
                      ".swal2-select, .swal2-input, .swal2-file, .swal2-range, .swal2-radio, .swal2-checkbox, .swal2-textarea";
                    popup.querySelectorAll(selector).forEach((el) => {
                      el.remove();
                    });
                    // Hide any that remain (e.g. if Swal re-adds after didOpen)
                    popup.querySelectorAll(selector).forEach((el) => {
                      el.style.setProperty("display", "none", "important");
                    });
                  };
                  hideThankYouInputs();
                  setTimeout(hideThankYouInputs, 0);
                },
              }).then(() => {
                window.location.reload();
              });
            } else {
              alert(thankYouMessage);
              window.location.reload();
            }
          }
        }
      } catch (error) {
        LoadingSpinner.hideSubmission();
        // Re-enable autosave when submit fails so user can keep editing safely.
        this.setupDraftAutoSave();

        if (window.Swal) {
          window.Swal.fire({
            icon: "error",
            title: "Error",
            text: error.message || "Form submission failed",
          });
        } else {
          alert(error.message || "Form submission failed");
        }
      }
    });
  }

  /**
   * Sync selected line items to shopping cart
   */
  async syncCartLineItems() {
    const selectedOptions = document.querySelectorAll(
      "select option:checked[data-lineitemid]",
    );
    if (!selectedOptions || selectedOptions.length === 0) return;

    const lineItemIDs = [];
    const quantities = [];

    selectedOptions.forEach((option) => {
      const lineItemId = option.getAttribute("data-lineitemid");
      if (lineItemId === null || lineItemId === undefined) return;

      const normalizedId = String(lineItemId).trim();
      if (
        !normalizedId ||
        normalizedId === "0" ||
        normalizedId.toLowerCase() === "null" ||
        normalizedId.toLowerCase() === "undefined"
      ) {
        return;
      }

      lineItemIDs.push(normalizedId);
      quantities.push("1");
    });

    if (lineItemIDs.length === 0) return;

    const params = new URLSearchParams();
    params.append("mode", "updateShoppingCart");
    params.append("resetShoppingCart", "true");
    lineItemIDs.forEach((id) => params.append("lineItemID[]", id));
    quantities.forEach((qty) => params.append("quantity[]", qty));

    const formData = new FormData();
    formData.append("mode", "updateShoppingCart");
    formData.append("resetShoppingCart", "true");
    lineItemIDs.forEach((id) => formData.append("lineItemID[]", id));
    quantities.forEach((qty) => formData.append("quantity[]", qty));

    const response = await fetch("/", {
      method: "POST",
      credentials: "include",
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Cart update failed with status ${response.status}`);
    }
  }

  /**
   * Setup AI language selector
   */
  setupAILanguageSelector() {
    if (!this.container) return;

    // Respect builder setting: only show when explicitly enabled
    const allowValue = this.formConfig?.allow_auto_translate;
    const allowAutoTranslate =
      allowValue === "Y" ||
      allowValue === true ||
      allowValue === 1 ||
      allowValue === "1";

    // Require explicit opt-in (URL param, container data attr, or init option)
    const urlParams = new URLSearchParams(window.location.search);
    const urlOptIn = ["1", "true", "yes"].includes(
      (urlParams.get("ai_translate") || "").toLowerCase(),
    );
    const containerOptIn = this.container?.dataset?.aiTranslate === "1";
    const optionOptIn = this.options?.enableAITranslate === true;

    if (!(allowAutoTranslate && (urlOptIn || containerOptIn || optionOptIn))) {
      return;
    }

    const languages = [
      { code: "en", name: "English" },
      { code: "he", name: "עברית" },
      { code: "fr", name: "Français" },
      { code: "es", name: "Español" },
      { code: "nl", name: "Nederlands" },
      { code: "ar", name: "العربية" },
      { code: "ru", name: "Русский" },
      { code: "am", name: "አማርኛ" },
    ];

    // Check if selector already exists
    if (document.getElementById("ai-language-selector")) {
      return;
    }

    const selectorHTML = `
      <div class="form-group ai-language-selector-container">
        <label for="ai-language-selector" class="form-label">
          <i class="fas fa-language me-2"></i>Translate Form:
        </label>
        <select id="ai-language-selector" class="form-select">
          <option value="">Select Language...</option>
          ${languages
            .map(
              (lang) =>
                `<option value="${lang.code}" ${this.formConfig.language === lang.code ? "selected" : ""}>${lang.name}</option>`,
            )
            .join("")}
        </select>
        <div id="ai-translation-status" class="mt-2 d-none">
          <div class="alert alert-info mb-0">
            <div class="d-flex align-items-center">
              <div class="spinner-border spinner-border-sm me-2" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <span>Translating form...</span>
            </div>
          </div>
        </div>
        <div id="ai-translation-error" class="mt-2 d-none">
          <div class="alert alert-danger mb-0">
            <span id="ai-translation-error-message"></span>
          </div>
        </div>
      </div>
    `;

    // Insert at the top of the form container
    this.container.insertAdjacentHTML("afterbegin", selectorHTML);

    // Setup event listener
    const selector = document.getElementById("ai-language-selector");
    if (selector) {
      selector.addEventListener("change", async (e) => {
        const selectedLanguage = e.target.value;
        if (!selectedLanguage) {
          // Reset to original language
          this.resetToOriginalLanguage();
          return;
        }

        await this.applyAITranslation(selectedLanguage);
      });
    }
  }

  /**
   * Apply AI translation to form
   * @param {string} targetLanguage - Target language code
   */
  async applyAITranslation(targetLanguage) {
    const statusEl = document.getElementById("ai-translation-status");
    const errorEl = document.getElementById("ai-translation-error");
    const errorMsgEl = document.getElementById("ai-translation-error-message");

    // Show loading state
    if (statusEl) statusEl.classList.remove("d-none");
    if (errorEl) errorEl.classList.add("d-none");

    try {
      // Get translations
      const result = await this.aiTranslator.translate(targetLanguage);

      if (result.status === "success" && result.translations) {
        // Apply translations to form
        this.applyTranslationsToDOM(result.translations);

        // Store selected language in sessionStorage
        sessionStorage.setItem(`form_language_${this.formId}`, targetLanguage);

        // Hide loading state
        if (statusEl) statusEl.classList.add("d-none");
      } else {
        // Show error
        if (errorEl) errorEl.classList.remove("d-none");
        if (errorMsgEl) {
          errorMsgEl.textContent =
            result.message || "Translation failed. Please try again.";
        }
        if (statusEl) statusEl.classList.add("d-none");

        // Fallback to existing translation files
        await this.translator.loadLanguage(targetLanguage);
      }
    } catch (error) {
      if (errorEl) errorEl.classList.remove("d-none");
      if (errorMsgEl) {
        errorMsgEl.textContent = "Translation failed. Please try again.";
      }
      if (statusEl) statusEl.classList.add("d-none");
    }
  }

  /**
   * Apply translations to DOM elements
   * @param {Object} translations - Translation mapping
   */
  applyTranslationsToDOM(translations) {
    // Translate field labels, headings, and text blocks
    this.formConfig.fields?.forEach((field) => {
      // Translate field labels (for input fields)
      const label = document.querySelector(`label[for="${field.id}"]`);
      if (label && field.title) {
        // Try multiple matching strategies for labels
        let translatedText = null;

        // Strategy 1: Exact match with field title
        if (translations[field.title]) {
          translatedText = translations[field.title];
        }
        // Strategy 2: Match without asterisk
        else if (label.textContent.includes(" *")) {
          const titleWithoutAsterisk = field.title.replace(/\s*\*$/, "").trim();
          if (translations[titleWithoutAsterisk]) {
            translatedText = translations[titleWithoutAsterisk];
          }
        }
        // Strategy 3: Match the label text as-is (in case title doesn't match)
        else {
          const labelText = label.textContent
            .trim()
            .replace(/\s*\*$/, "")
            .trim();
          if (translations[labelText]) {
            translatedText = translations[labelText];
          }
        }

        if (translatedText) {
          // Preserve required asterisk if it was there
          const hasAsterisk =
            label.textContent.includes(" *") || field.required === "y";
          label.textContent = translatedText + (hasAsterisk ? " *" : "");
        }
      }

      // Translate headings (type 'h' fields)
      if (field.type === "h" && field.title && translations[field.title]) {
        const headingEl = document.querySelector(`[data-id="${field.id}"]`);
        if (headingEl) {
          // Heading fields are rendered as h1, h2, etc with the title as content
          headingEl.textContent = translations[field.title];
        }
      }

      // Translate text blocks (type 't' fields) - these contain HTML content
      if (field.type === "t" && field.content) {
        // Text blocks are rendered directly as HTML into .formCol elements
        // Try multiple selectors to find the element
        const textBlockEl =
          document.querySelector(`.formCol[data-id="${field.id}"]`) ||
          document.querySelector(`[data-id="${field.id}"][data-type="t"]`) ||
          document.querySelector(`[data-id="${field.id}"]`);

        if (textBlockEl && field.content) {
          // Get the current HTML content (might have been modified)
          const currentHTML = textBlockEl.innerHTML || field.content;

          // Create a temporary DOM element to work with the HTML
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = currentHTML;

          // Extract all text content for matching
          const allText = (
            tempDiv.textContent ||
            tempDiv.innerText ||
            ""
          ).trim();

          // Try to translate the entire text block first (most reliable)
          if (allText && translations[allText]) {
            textBlockEl.innerHTML = translations[allText];
          } else {
            // Walk through the DOM tree and translate text nodes while preserving HTML structure
            const walker = document.createTreeWalker(
              tempDiv,
              NodeFilter.SHOW_TEXT,
              {
                acceptNode: function (node) {
                  // Skip empty text nodes
                  if (!node.textContent || !node.textContent.trim()) {
                    return NodeFilter.FILTER_REJECT;
                  }
                  return NodeFilter.FILTER_ACCEPT;
                },
              },
              false,
            );

            let textNode;
            let hasTranslation = false;

            // Translate each text node
            while ((textNode = walker.nextNode())) {
              const originalText = textNode.textContent.trim();

              // Try exact match first
              if (originalText && translations[originalText]) {
                textNode.textContent = translations[originalText];
                hasTranslation = true;
              } else if (originalText.length > 2) {
                // Try to find matching translation keys
                // Sort by length (longest first) to match longer phrases first
                const sortedKeys = Object.keys(translations)
                  .filter((key) => key.length > 2)
                  .sort((a, b) => b.length - a.length);

                for (const key of sortedKeys) {
                  // Check if the text node contains this key
                  if (originalText === key || originalText.includes(key)) {
                    // Replace the matching part
                    const translated = translations[key];
                    textNode.textContent = originalText.replace(
                      key,
                      translated,
                    );
                    hasTranslation = true;
                    break;
                  }
                }
              }
            }

            // If we found translations, update the element
            if (hasTranslation) {
              textBlockEl.innerHTML = tempDiv.innerHTML;
            } else {
              // Fallback: try direct string replacement in HTML
              let translatedContent = currentHTML;
              let foundAny = false;

              // Sort translations by length (longest first) to avoid partial replacements
              const sortedTranslations = Object.entries(translations)
                .filter(([key]) => key && key.length > 2)
                .sort((a, b) => b[0].length - a[0].length);

              for (const [original, translated] of sortedTranslations) {
                // Escape special regex characters
                const escaped = original.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

                // Try multiple replacement strategies
                // Strategy 1: Text between tags (most common)
                const regex1 = new RegExp(
                  `(>)([^<]*?)${escaped}([^<]*?)(<)`,
                  "gi",
                );
                if (regex1.test(translatedContent)) {
                  translatedContent = translatedContent.replace(
                    regex1,
                    (match, p1, p2, p3, p4) => {
                      return p1 + p2 + translated + p3 + p4;
                    },
                  );
                  foundAny = true;
                }

                // Strategy 2: Direct text replacement (if not already replaced)
                if (!foundAny || !translatedContent.includes(translated)) {
                  const regex2 = new RegExp(escaped, "g");
                  if (regex2.test(translatedContent)) {
                    translatedContent = translatedContent.replace(
                      regex2,
                      translated,
                    );
                    foundAny = true;
                  }
                }
              }

              if (foundAny) {
                textBlockEl.innerHTML = translatedContent;
              }
            }
          }
        }
      }

      // Translate placeholders
      // Support both 'placeholder' and 'placehoder' (typo) for backward compatibility
      const input = document.querySelector(
        `[name="${field.id}"], [id="${field.id}"]`,
      );
      const placeholderKey = field.placeholder || field.placehoder;
      if (input && placeholderKey && translations[placeholderKey]) {
        input.placeholder = translations[placeholderKey];
      }

      // Translate select options (preserve original values, only translate display text)
      if (field.values && field.type === "select") {
        const select = document.querySelector(`select[name="${field.id}"]`);
        if (select && field.values) {
          // Match options by their value attribute (which contains the original value)
          Array.from(select.options).forEach((option) => {
            if (option.value) {
              // Find the original value in field.values array
              const originalValue = field.values.find(
                (val) => val === option.value,
              );
              if (originalValue && translations[originalValue]) {
                // Only update the display text, keep the value attribute unchanged
                option.textContent = translations[originalValue];
              }
            }
          });
        }
      }

      // Translate checkbox/radio options (preserve original values, only translate display text)
      if (
        (field.type === "checkbox" || field.type === "radio") &&
        field.values
      ) {
        field.values.forEach((value) => {
          if (translations[value]) {
            // Find the input by its value attribute (which contains the original value)
            const input = document.querySelector(
              `input[type="${field.type}"][value="${CSS.escape(value)}"]`,
            );
            if (input) {
              // Only update the label text, keep the value attribute unchanged
              const label =
                input.closest("label") ||
                input.parentElement.querySelector("label");
              if (label) {
                label.textContent = translations[value];
              }
            }
          }
        });
      }

      // Translate button fields (type 'button')
      if (field.type === "button" && field.title && translations[field.title]) {
        const button = document.querySelector(
          `button[type="button"][data-id="${field.id}"], .formCol[data-id="${field.id}"] button`,
        );
        if (button) {
          button.textContent = translations[field.title];
        }
      }
    });

    // Translate page titles
    this.formConfig.pages?.forEach((page) => {
      if (page.title && translations[page.title]) {
        // Find page element and update title if it exists
        const pageEl = document.querySelector(`.page[data-id="${page.id}"]`);
        if (pageEl) {
          const titleEl = pageEl.querySelector("h1, h2, h3, h4, h5, h6");
          if (titleEl) {
            titleEl.textContent = translations[page.title];
          }
        }
      }
    });

    // Translate buttons
    // Submit button - try multiple strategies
    const submitButton = document.querySelector(".submit-button");
    if (submitButton) {
      const submitValue = submitButton.value || submitButton.textContent || "";
      const submitText = submitValue.trim();

      // Try to find translation
      let translatedSubmit = null;
      if (translations[submitText]) {
        translatedSubmit = translations[submitText];
      } else {
        // Try common submit button texts
        const commonSubmitTexts = [
          "Submit",
          "Send",
          "Submit Form",
          "Send Form",
          "שלח",
          "אישור",
        ];
        for (const commonText of commonSubmitTexts) {
          if (translations[commonText]) {
            translatedSubmit = translations[commonText];
            break;
          }
        }
      }

      if (translatedSubmit) {
        if (submitButton.tagName === "INPUT") {
          submitButton.value = translatedSubmit;
        } else {
          submitButton.textContent = translatedSubmit;
        }
      }
    }

    // Next/Previous page buttons
    const nextButtons = document.querySelectorAll(".next_page");
    nextButtons.forEach((btn) => {
      const btnText = (btn.textContent || btn.value || "").trim();
      if (btnText && translations[btnText]) {
        if (btn.tagName === "INPUT") {
          btn.value = translations[btnText];
        } else {
          btn.textContent = translations[btnText];
        }
      } else {
        // Try common next button texts
        const commonNextTexts = ["Next", "Continue", "הבא", "המשך"];
        for (const commonText of commonNextTexts) {
          if (translations[commonText]) {
            if (btn.tagName === "INPUT") {
              btn.value = translations[commonText];
            } else {
              btn.textContent = translations[commonText];
            }
            break;
          }
        }
      }
    });

    const prevButtons = document.querySelectorAll(".prev_page");
    prevButtons.forEach((btn) => {
      const btnText = (btn.textContent || btn.value || "").trim();
      if (btnText && translations[btnText]) {
        if (btn.tagName === "INPUT") {
          btn.value = translations[btnText];
        } else {
          btn.textContent = translations[btnText];
        }
      } else {
        // Try common previous button texts
        const commonPrevTexts = ["Previous", "Back", "קודם", "חזור"];
        for (const commonText of commonPrevTexts) {
          if (translations[commonText]) {
            if (btn.tagName === "INPUT") {
              btn.value = translations[commonText];
            } else {
              btn.textContent = translations[commonText];
            }
            break;
          }
        }
      }
    });

    const signButtons = document.querySelectorAll(".sign_me");
    signButtons.forEach((btn) => {
      const btnText = (btn.textContent || btn.value || "").trim();
      if (btnText && translations[btnText]) {
        btn.textContent = translations[btnText];
      } else {
        // Try common sign button texts
        const commonSignTexts = ["Click to Sign", "Sign", "חתום", "לחתום"];
        for (const commonText of commonSignTexts) {
          if (translations[commonText]) {
            btn.textContent = translations[commonText];
            break;
          }
        }
      }
    });
  }

  /**
   * Apply form headers from branding settings
   * Priority: 1) branding header if exists, 2) form title if branding header is empty
   */
  applyFormHeaders() {
    const mainHeaderEl = document.querySelector("h1.main_header");

    if (mainHeaderEl) {
      // Priority 1: Use branding main header if it exists and is not empty
      if (
        this.formConfig?.branding?.headers?.mainHeader &&
        this.formConfig.branding.headers.mainHeader.trim() !== ""
      ) {
        mainHeaderEl.textContent = this.formConfig.branding.headers.mainHeader;
      }
      // Priority 2: If branding header is empty/missing, use form title
      else if (this.formConfig?.title && this.formConfig.title.trim() !== "") {
        mainHeaderEl.textContent = this.formConfig.title;
      }
    }

    // Apply sub header to any element with class sub_header
    if (
      this.formConfig?.branding?.headers?.subHeader &&
      this.formConfig.branding.headers.subHeader.trim() !== ""
    ) {
      const subHeaderElements = document.querySelectorAll(".sub_header");
      subHeaderElements.forEach((el) => {
        el.textContent = this.formConfig.branding.headers.subHeader;
      });
    }
  }

  /**
   * When the current page has exactly one area with an image, set that image as the background
   * of the div with id one_page_area_image (if present on the page), and set #area_toc and
   * #area_description from the area's toc and description. Clears when the current page has
   * zero or multiple areas. Re-run on page navigation.
   *
   * Optional Mobile Logo (same data shape as Logo): if present, applies area.mobileImage as
   * background-image on #one_page_area_image_mobile so you can place that div in HTML and
   * show/hide desktop vs mobile with CSS only.
   */
  applyOnePageAreaImage() {
    const el = document.getElementById("one_page_area_image");
    const elMobile = document.getElementById("one_page_area_image_mobile");
    const setTextById = (id, text) => {
      const node = document.getElementById(id);
      if (node) node.textContent = text;
    };

    const clearMobileAreaBackground = () => {
      if (!elMobile) return;
      elMobile.style.backgroundImage = "";
      elMobile.style.backgroundSize = "";
      elMobile.style.backgroundPosition = "";
    };

    const setMobileAreaBackground = (rawUrl) => {
      const url = typeof rawUrl === "string" ? rawUrl.trim() : "";
      if (!elMobile) return;
      if (!url) {
        clearMobileAreaBackground();
        return;
      }
      elMobile.style.backgroundImage = `url("${url.replace(/"/g, '\\"')}")`;
      elMobile.style.backgroundSize = "cover";
      elMobile.style.backgroundPosition = "center";
    };

    const clearAreaPlaceholders = () => {
      setTextById("area_toc", "");
      setTextById("area_description", "");
      setTextById("area_toc_mobile", "");
      setTextById("area_description_mobile", "");
    };

    if (!el) return;
    if (
      !this.formConfig?.areas ||
      !this.formConfig.pages ||
      !this.pageManager
    ) {
      el.style.backgroundImage = "";
      clearAreaPlaceholders();
      clearMobileAreaBackground();
      return;
    }
    const sortedPages = FormConfig.sortPages(this.formConfig.pages);
    if (!sortedPages.length) {
      el.style.backgroundImage = "";
      clearAreaPlaceholders();
      clearMobileAreaBackground();
      return;
    }
    const currentPageNum = this.pageManager.getCurrentPage();
    if (currentPageNum < 1 || currentPageNum > sortedPages.length) {
      el.style.backgroundImage = "";
      clearAreaPlaceholders();
      clearMobileAreaBackground();
      return;
    }
    const currentPageConfig = sortedPages[currentPageNum - 1];
    const pageId = currentPageConfig?.id;
    if (!pageId) {
      el.style.backgroundImage = "";
      clearAreaPlaceholders();
      clearMobileAreaBackground();
      return;
    }
    const areasOnPage = FormConfig.getAreasForPage(this.formConfig, pageId);
    if (areasOnPage.length !== 1) {
      el.style.backgroundImage = "";
      el.style.backgroundSize = "";
      el.style.backgroundPosition = "";
      clearAreaPlaceholders();
      clearMobileAreaBackground();
      return;
    }
    const area = areasOnPage[0];
    const url = typeof area.image === "string" ? area.image.trim() : "";
    if (!url) {
      el.style.backgroundImage = "";
      el.style.backgroundSize = "";
      el.style.backgroundPosition = "";
      clearAreaPlaceholders();
      clearMobileAreaBackground();
      return;
    }
    el.style.backgroundImage = `url("${url.replace(/"/g, '\\"')}")`;
    el.style.backgroundSize = "cover";
    el.style.backgroundPosition = "center";

    const legacyMobileImg = el.querySelector("img.mobile-logo");
    if (legacyMobileImg) legacyMobileImg.remove();

    // Mobile Logo: same mechanism as Logo, on a separate element you place in the template.
    setMobileAreaBackground(area.mobileImage);

    const tocRaw = typeof area.toc === "string" ? area.toc.trim() : "";
    const descRaw =
      typeof area.description === "string" ? area.description.trim() : "";
    const toc = FieldRenderer.replacePlaceholdersInString(
      tocRaw,
      this.activeCustomer,
      this.behalfContext,
    );
    const description = FieldRenderer.replacePlaceholdersInString(
      descRaw,
      this.activeCustomer,
      this.behalfContext,
    );
    setTextById("area_toc", toc);
    setTextById("area_description", description);
    setTextById("area_toc_mobile", toc);
    setTextById("area_description_mobile", description);
  }

  /**
   * Reset form to original language
   */
  async resetToOriginalLanguage() {
    const originalLanguage = this.formConfig.language || "en";
    await this.translator.loadLanguage(originalLanguage);

    // Reload form to reset all text
    location.reload();
  }

  /**
   * Setup submit button
   */
  setupSubmitButton() {
    const pages = document.querySelectorAll(".page");
    if (pages.length === 0) return;

    const lastPage = pages[pages.length - 1];
    const submitHolder = lastPage.querySelector(".submit_holder");

    if (!submitHolder) {
      const submitText = this.translator.t("buttons.submit");
      const submitHTML = `<div class="form-group submit_holder page-navigation-container"><div class="prev_page_placeholder"></div><input type="submit" disabled class="submit-button next_page" value="${submitText}"></div>`;
      lastPage.insertAdjacentHTML("beforeend", submitHTML);
    } else {
      // Update existing submit button text if it exists
      const submitButton = submitHolder.querySelector('input[type="submit"]');
      if (submitButton) {
        submitButton.value = this.translator.t("buttons.submit");
        // Ensure submit holder has the navigation container class
        if (!submitHolder.classList.contains("page-navigation-container")) {
          submitHolder.classList.add("page-navigation-container");
          // Add placeholder if not exists
          if (!submitHolder.querySelector(".prev_page_placeholder")) {
            submitHolder.insertAdjacentHTML(
              "afterbegin",
              '<div class="prev_page_placeholder"></div>',
            );
          }
          // Add next_page class to submit button for consistent styling
          submitButton.classList.add("next_page");
        }
      }
    }

    document.addEventListener(
      "input",
      () => {
        this.updateSubmitButtonState();
      },
      false,
    );

    document.addEventListener(
      "change",
      () => {
        this.updateSubmitButtonState();
      },
      false,
    );

    if (this.container) {
      this.container.addEventListener("form.fieldVisibilityChanged", () => {
        this.updateSubmitButtonState();
      });
    }

    this.updateSubmitButtonState();
  }

  /**
   * Update submit button state
   */
  updateSubmitButtonState() {
    const submitButtons = document.querySelectorAll(
      '.submit_holder input[type="submit"]',
    );

    submitButtons.forEach((button) => {
      const requiredFields = document.querySelectorAll("[required]");
      const emptyFields = Array.from(requiredFields).filter((field) => {
        const verifyEl =
          field.tagName === "SELECT" && field.classList.contains("tqf_select")
            ? field.closest(".form-group")
            : field;
        const style = window.getComputedStyle(verifyEl);
        const isVisible =
          style.display !== "none" &&
          style.visibility !== "hidden" &&
          !verifyEl.closest(".d-none");
        if (!isVisible) return false;

        if (field.type === "checkbox" || field.type === "radio") {
          const fieldName = field.name || field.id;
          const inputs = document.getElementsByName(fieldName);
          return !Array.from(inputs).some((el) => el.checked);
        }

        return !field.value || field.value.trim() === "";
      });

      // Required checkbox groups don't have [required] on inputs; check formConfig
      let emptyRequiredCheckboxGroup = false;
      if (this.formConfig && this.formConfig.fields) {
        for (const fieldConfig of this.formConfig.fields) {
          if (fieldConfig.required !== "y" || fieldConfig.type !== "checkbox")
            continue;
          const groupName = fieldConfig.id + "[]";
          const inputs = document.getElementsByName(groupName);
          if (inputs.length === 0) continue;
          const firstInput = inputs[0];
          const groupContainer = firstInput.closest(".form-group");
          const isVisible =
            groupContainer &&
            window.getComputedStyle(groupContainer).display !== "none" &&
            window.getComputedStyle(groupContainer).visibility !== "hidden" &&
            !groupContainer.closest(".d-none");
          if (isVisible && !Array.from(inputs).some((el) => el.checked)) {
            emptyRequiredCheckboxGroup = true;
            break;
          }
        }
      }

      const fieldsWithErrors = document.querySelectorAll(
        ".error-container.validation-error-message:not(:empty)",
      );
      const visibleErrors = Array.from(fieldsWithErrors).filter(
        (err) => !err.closest(".d-none"),
      );

      if (
        emptyFields.length === 0 &&
        !emptyRequiredCheckboxGroup &&
        visibleErrors.length === 0
      ) {
        button.disabled = false;
      } else {
        button.disabled = true;
      }
    });
  }

  /**
   * Update next page button(s) on the active page: disabled until all signatures on that page are done.
   */
  updateNextButtonStateForSignatures() {
    try {
      if (!this.pageManager || !this.container) return;

      const currentPageNum = this.pageManager.getCurrentPage();
      const currentPageElement = this.container.querySelector(
        `.page[data-num="${currentPageNum}"]`,
      );
      if (!currentPageElement) return;

      const nextButtons = currentPageElement.querySelectorAll(".next_page");
      const navNextButtons = Array.from(nextButtons).filter(
        (el) => el.tagName === "BUTTON" && el.getAttribute("data-to"),
      );
      if (navNextButtons.length === 0) return;

      const formGroups = currentPageElement.querySelectorAll(".form-group");
      let hasSignatureFields = false;
      formGroups.forEach((group) => {
        if (
          !group.querySelector(".sign_me") &&
          !group.querySelector("input.signature_placeholder")
        )
          return;
        const style = window.getComputedStyle(group);
        if (
          style.display !== "none" &&
          style.visibility !== "hidden" &&
          !group.closest(".d-none")
        ) {
          hasSignatureFields = true;
        }
      });

      if (!hasSignatureFields) {
        navNextButtons.forEach((btn) => {
          btn.disabled = false;
        });
        return;
      }

      const sigResult = this.pageManager.getSignatureValidationOnCurrentPage();
      const allSigned = sigResult.isValid;
      navNextButtons.forEach((btn) => {
        btn.disabled = !allSigned;
      });
    } catch (err) {}
  }

  /**
   * Setup session timer
   */
  setupSessionTimer() {
    let timeoutHandle;
    const timeoutDuration = 20 * 60 * 1000; // 20 minutes

    const startTimer = () => {
      clearTimeout(timeoutHandle);
      timeoutHandle = setTimeout(() => {
        this.showSessionWarning();
      }, timeoutDuration);
    };

    startTimer();

    ["mousedown", "keydown", "scroll", "touchstart"].forEach((event) => {
      document.addEventListener(event, startTimer, { passive: true });
    });
  }

  /**
   * Show session warning
   */
  showSessionWarning() {
    if (window.Swal) {
      let timerInterval;
      window.Swal.fire({
        title: "You are about to close the form due to inactivity",
        html: "Closing in <b></b> seconds.",
        timer: 2 * 60 * 1000,
        timerProgressBar: true,
        didOpen: () => {
          window.Swal.showLoading();
          const b = window.Swal.getHtmlContainer().querySelector("b");
          timerInterval = setInterval(() => {
            b.textContent = Math.round(window.Swal.getTimerLeft() / 1000);
          }, 100);
        },
        willClose: () => {
          clearInterval(timerInterval);
        },
        showCancelButton: true,
        cancelButtonText: "Want to continue",
      }).then((result) => {
        if (result.dismiss === window.Swal.DismissReason.cancel) {
          this.setupSessionTimer();
        } else {
          window.close();
        }
      });
    }
  }

  /**
   * Setup field-specific features
   */
  setupFieldSpecificFeatures() {
    if (!this.formConfig) return;

    this.formConfig.fields.forEach((field) => {
      if (field.maxchars && field.maxchars.trim() !== "") {
        const maxLength = parseInt(field.maxchars);
        const element = document.querySelector(`[name="${field.id}"]`);
        if (element && maxLength > 0) {
          element.addEventListener("input", (e) => {
            if (e.target.value.length > maxLength) {
              e.target.value = e.target.value.substring(0, maxLength);
            }
          });
        }
      }
    });

    const todayDateFields = this.container.querySelectorAll(
      '[data-input-function="forceTodayDate"]',
    );
    const today = FormatUtils.getTodayDate();
    todayDateFields.forEach((field) => {
      field.readOnly = true;
      if (field._flatpickr) {
        field._flatpickr.setDate(today, false);
      } else {
        field.value = today;
      }
    });

    const yearDiffFields = this.container.querySelectorAll(
      '[data-input-function="yearDifferenceFromField"]',
    );
    yearDiffFields.forEach((field) => {
      const sourceKey = field.getAttribute("data-year-diff-source");
      const syncSource =
        field.getAttribute("data-field-sync-source") || "behalf";
      if (!sourceKey) return;
      const pseudoConfig = {
        defaultValue: sourceKey,
        field_sync_source: syncSource,
      };
      const dateStr = FieldRenderer.getDefaultValue(
        pseudoConfig,
        this.activeCustomer || {},
        this.behalfContext,
        this.formSourceContext,
      );
      const age = FieldRenderer.computeAgeFromDateString(dateStr);
      field.value = age;
    });
  }

  /**
   * Setup auto-resize textarea
   */
  setupAutoResizeTextarea() {
    document.addEventListener(
      "input",
      (e) => {
        if (e.target.tagName.toLowerCase() === "textarea") {
          e.target.style.height = "auto";
          e.target.style.height = `${e.target.scrollHeight}px`;
        }
      },
      true,
    );
  }

  /**
   * Setup required attributes observer
   */
  setupRequiredAttributesObserver() {
    const updateRequiredAttributes = () => {
      document
        .querySelectorAll("form input, form select, form textarea")
        .forEach((element) => {
          const verifyElement = element.classList.contains(
            "signature_placeholder",
          )
            ? element.closest(".form-group")
            : element.tagName === "SELECT" &&
                element.classList.contains("tqf_select")
              ? element.closest(".form-group")
              : element;

          if (verifyElement) {
            const style = window.getComputedStyle(verifyElement);
            const isVisible =
              style.display !== "none" &&
              style.visibility !== "hidden" &&
              !verifyElement.closest(".d-none");

            if (isVisible) {
              // Never add required to checkbox inputs (multi-checkbox groups use "at least one" in JS only)
              if (
                element.type !== "checkbox" &&
                element.dataset.initialRequired === "true"
              ) {
                element.setAttribute("required", "required");
              }
            } else {
              element.removeAttribute("required");
            }
          } else {
            element.removeAttribute("required");
          }
        });
    };

    document.addEventListener("keyup", updateRequiredAttributes, true);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class"
        ) {
          updateRequiredAttributes();
        }
      });
    });

    observer.observe(document.body, {
      attributes: true,
      subtree: true,
      attributeFilter: ["class"],
    });
  }

  /**
   * Re-initialize field handlers for a container (used for cloned repeater fields)
   * @param {Element} container - Container element
   */
  reinitializeFieldHandlers(container) {
    // Re-initialize phone inputs
    const phoneInputs = container.querySelectorAll(".phone-input");
    phoneInputs.forEach((input) => {
      if (!input.itiInstance && window.intlTelInput) {
        const iti = window.intlTelInput(input, {
          initialCountry: "auto",
          geoIpLookup: (callback) => {
            fetch("https://ipapi.co/json/")
              .then((res) => res.json())
              .then((data) => callback(data.country_code))
              .catch(() => callback("us"));
          },
          utilsScript:
            "https://cdn.jsdelivr.net/npm/intl-tel-input@24.6.0/build/js/utils.js",
        });
        input.itiInstance = iti;

        // Helper function to update data-full-number whenever there's a value
        const updateFullNumber = () => {
          const fullNumber = iti.getNumber();
          if (fullNumber) {
            input.setAttribute("data-full-number", fullNumber);
          } else {
            input.removeAttribute("data-full-number");
          }
        };

        input.addEventListener("input", () => {
          ErrorManager.clearError(input);
          // Update data-full-number on input (debounced)
          clearTimeout(input._updateNumberTimeout);
          input._updateNumberTimeout = setTimeout(updateFullNumber, 300);
        });

        input.addEventListener("blur", () => {
          if (input.value.trim() && !iti.isValidNumber()) {
            ErrorManager.showError(input, "Enter valid phone number");
          } else {
            ErrorManager.clearError(input);
            // Update data-full-number on blur if valid
            updateFullNumber();
          }
        });

        input.addEventListener("countrychange", () => {
          updateFullNumber();
        });
      }
    });

    // Re-initialize calculated fields (they use event delegation, so just trigger initial calculation)
    if (this.formConfig) {
      this.formConfig.fields.forEach((field) => {
        if (
          field.type === "calculated" &&
          field.calculation &&
          field.calculation.length > 0
        ) {
          const calculatedField = container.querySelector(
            `[name="${field.id}"]`,
          );
          if (calculatedField) {
            CalculatedFieldHelper.calculateField(field.id);
          }
        }
      });
    }

    // Re-initialize shadow fields (they use event delegation, so just trigger initial sync)
    if (this.formConfig) {
      this.formConfig.fields.forEach((field) => {
        if (
          field.type === "shadow" &&
          field.syncFields &&
          field.syncFields.length > 0
        ) {
          const shadowField = container.querySelector(`[name="${field.id}"]`);
          if (shadowField) {
            ShadowFieldHelper.syncShadowField(field.id);
          }
        }
      });
    }

    FieldSyncHelper.runInitialSync();
    CustomersBasedFillHelper.runInitialSync();
  }

  /**
   * Set hidden field defaults on submit
   */
  setHiddenFieldDefaults() {
    if (!this.formConfig || !this.formConfig.fields) return;

    this.formConfig.fields.forEach((field) => {
      if (field.visibleSettings === "n" && field.defaultValueIfEmpty) {
        const element = document.querySelector(`[name="${field.id}"]`);
        if (element && !element.value) {
          element.value = field.defaultValueIfEmpty;
        }
      }
    });
  }

  /**
   * Whether the form is public (no draft save/load).
   * access_type null is treated as public, matching backend behavior.
   */
  isPublicForm() {
    return (this.formConfig?.access_type ?? "public") !== "private";
  }

  /**
   * Setup draft auto-save functionality
   */
  setupDraftAutoSave() {
    if (this.isPublicForm()) return;
    if (!this.formSubmitter) return;
    if (this._draftAutoSaveInitialized) return;
    this._draftAutoSaveInitialized = true;

    let autoSaveTimer = null;
    let debounceTimer = null;
    let hasUnsavedChanges = false;
    let isSaving = false;
    let saveGeneration = 0;
    const setupTime = Date.now();
    const GRACE_MS = 3000; // Ignore change events for 3s after init to avoid saving during form population
    // When editing an existing entry (?entry=xxx or loaded draft), use its id so first save updates it
    let lastSavedEntryId = this.formState.getEntryId()
      ? String(this.formState.getEntryId())
      : null;

    // Create draft status indicator
    const createDraftIndicator = () => {
      const indicator = document.createElement("div");
      indicator.id = "draft-status-indicator";
      indicator.className = "draft-status-indicator";
      indicator.style.cssText =
        "position: fixed; bottom: 20px; right: 20px; padding: 10px 15px; background: #f8f9fa; border: 1px solid #dee2e6; border-radius: 4px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); z-index: 1000; font-size: 14px; display: none;";
      document.body.appendChild(indicator);
      return indicator;
    };

    const draftIndicator = createDraftIndicator();

    const updateDraftIndicator = (message, type = "info") => {
      if (!draftIndicator) return;
      if (type === "saved" || type === "saving") {
        draftIndicator.style.display = "none";
        return;
      }

      const colors = {
        saving: "#ffc107",
        saved: "#28a745",
        error: "#dc3545",
        info: "#17a2b8",
      };

      draftIndicator.textContent = message;
      draftIndicator.style.display = "block";
      draftIndicator.style.borderColor = colors[type] || colors.info;
      draftIndicator.style.background = colors[type]
        ? `${colors[type]}15`
        : "#f8f9fa";
    };

    // Auto-save function
    const performAutoSave = async () => {
      if (isSaving || !hasUnsavedChanges) return;

      isSaving = true;
      const genAtStart = saveGeneration;
      updateDraftIndicator("Saving draft...", "saving");
      this.container.dispatchEvent(
        new CustomEvent("tqtag.form.draftSaveStart", {
          bubbles: true,
          detail: { formId: this.formId },
        }),
      );

      const dispatchDraftSaveComplete = (success, entryId, error) => {
        this.container.dispatchEvent(
          new CustomEvent("tqtag.form.draftSaveComplete", {
            bubbles: true,
            detail: {
              formId: this.formId,
              success,
              entryId: entryId != null ? String(entryId) : undefined,
              error: error || undefined,
            },
          }),
        );
      };

      try {
        // Use FormSubmitter.collectFormData so file fields are included (existing "open file"
        // links when input is empty, plus new uploads). Otherwise draft save would drop file refs.
        const formData = await this.formSubmitter.collectFormData();
        if (genAtStart !== saveGeneration) {
          hasUnsavedChanges = true;
          return;
        }
        formData.draft = true;

        // Prefer lastSavedEntryId so we keep updating the same draft entry
        if (lastSavedEntryId) {
          formData.entry_id =
            typeof lastSavedEntryId === "string"
              ? lastSavedEntryId
              : String(lastSavedEntryId);
        }

        // Don't create new empty drafts: if no entry_id and form is empty, skip to avoid overwriting good drafts
        if (!formData.entry_id) {
          const entry = formData.entry || {};
          const hasContent = Object.values(entry).some((v) => {
            if (Array.isArray(v))
              return v.some((x) => x != null && String(x).trim() !== "");
            return v != null && String(v).trim() !== "";
          });
          if (!hasContent) {
            isSaving = false;
            return;
          }
        }

        if (genAtStart !== saveGeneration) {
          hasUnsavedChanges = true;
          return;
        }

        // Save draft
        const response = await API.saveFormDraft(formData);

        if (response.status === "ok") {
          hasUnsavedChanges = genAtStart !== saveGeneration;
          if (response.entry_id != null) {
            lastSavedEntryId =
              typeof response.entry_id === "string"
                ? response.entry_id
                : String(response.entry_id);
            this.formState.setEditMode(true, lastSavedEntryId);
          }
          // Refresh server metadata only (creation_date, last_updated, etc.) — not user-edited fields
          const metadataFields = (this.formConfig?.fields || []).filter(
            (f) => f?.entryMetadataSource && f.id,
          );
          if (lastSavedEntryId && metadataFields.length > 0) {
            await this.loadEntryData(lastSavedEntryId);
            if (this.loadedEntryData) {
              const metadataOnly = {};
              metadataFields.forEach((field) => {
                const v = this.loadedEntryData[field.id];
                if (v != null && v !== "") metadataOnly[field.id] = v;
              });
              if (Object.keys(metadataOnly).length > 0) {
                await this.populateFormData(metadataOnly);
              }
              this.loadedEntryData = null;
            }
          }
          updateDraftIndicator("Draft saved", "saved");
          dispatchDraftSaveComplete(true, lastSavedEntryId, null);
        } else {
          const errMsg = response.error || "Failed to save draft";
          updateDraftIndicator("Failed to save draft", "error");
          dispatchDraftSaveComplete(false, null, errMsg);
        }
      } catch (error) {
        const errMsg = error && (error.message || String(error));
        updateDraftIndicator("Error saving draft", "error");
        dispatchDraftSaveComplete(false, null, errMsg);
      } finally {
        isSaving = false;
      }
    };

    // Debounced auto-save on field blur
    const debouncedAutoSave = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        performAutoSave();
      }, 500);
    };

    // Mark form as having unsaved changes
    const markAsChanged = () => {
      hasUnsavedChanges = true;
      saveGeneration += 1;
    };

    // Guard: skip during grace period (avoids saving empty form from programmatic events during population)
    const inGracePeriod = () => Date.now() - setupTime < GRACE_MS;

    // Expose so RepeaterHelper (add/delete row) can trigger draft changed + debounced save
    this.notifyDraftChanged = () => {
      if (inGracePeriod()) return;
      markAsChanged();
      debouncedAutoSave();
    };

    // Helper: get current value for field (for event detail)
    const getFieldValueForEvent = (el) => {
      if (!el) return undefined;
      if (el.type === "checkbox") return el.checked ? el.value : "";
      if (el.type === "radio") return el.checked ? el.value : undefined;
      return el.value;
    };

    // Setup event listeners
    const handleDraftChange = (e) => {
      if (!e.target || !this.container.contains(e.target)) return;
      if (inGracePeriod()) return;
      if (
        e.target.matches("input, select, textarea") &&
        e.target.type !== "submit" &&
        !e.target.classList.contains("signature-pad")
      ) {
        const fieldId = e.target.name || e.target.id;
        const value = getFieldValueForEvent(e.target);
        this.container.dispatchEvent(
          new CustomEvent("tqtag.form.fieldChange", {
            bubbles: true,
            detail: { formId: this.formId, fieldId, value, element: e.target },
          }),
        );
        markAsChanged();
        debouncedAutoSave();
      }
    };
    document.addEventListener("blur", handleDraftChange, true);
    document.addEventListener("input", handleDraftChange, true);
    document.addEventListener("change", handleDraftChange, true);

    // Periodic auto-save every 30 seconds if there are unsaved changes
    autoSaveTimer = setInterval(() => {
      if (hasUnsavedChanges && !isSaving) {
        performAutoSave();
      }
    }, 30000);

    // Store for cleanup if needed
    this.draftAutoSaveCleanup = () => {
      if (autoSaveTimer) clearInterval(autoSaveTimer);
      if (debounceTimer) clearTimeout(debounceTimer);
      document.removeEventListener("blur", handleDraftChange, true);
      document.removeEventListener("input", handleDraftChange, true);
      document.removeEventListener("change", handleDraftChange, true);
      this.notifyDraftChanged = null;
      if (draftIndicator && draftIndicator.parentNode) {
        draftIndicator.parentNode.removeChild(draftIndicator);
      }
      this._draftAutoSaveInitialized = false;
      this.draftAutoSaveCleanup = null;
    };
  }

  /**
   * Get popup message for loaded entry/draft. Uses branding.loadedEntryMessage if configured.
   * @returns {{ title: string, text: string }}
   */
  _getLoadedEntryMessage() {
    const loaded = this.formConfig?.branding?.loadedEntryMessage;
    if (loaded?.title && loaded?.text) {
      return { title: loaded.title, text: loaded.text };
    }
    return {
      title: "Draft Loaded",
      text: "Your draft has been loaded. You can continue filling the form.",
    };
  }

  /**
   * Load last draft for the form
   * @param {Object} options - Optional: { skipApply: true } when form not yet rendered (init path)
   */
  async loadLastDraft(options = {}) {
    try {
      const response = await API.getLastDraft(
        this.formId,
        this.behalfToken,
        !!this.formConfig?.private_draft,
      );
      if (response.status === "ok" && response.data) {
        const draftEntry = response.data;

        // Set edit mode with draft entry_id
        if (draftEntry.entry_id) {
          this.formState.setEditMode(true, draftEntry.entry_id);
        }

        // Populate form with draft data
        const formData = draftEntry.form_data || draftEntry.data || draftEntry;
        this.formState.setFormData(formData);
        this.loadedEntryData = formData;

        // Apply to rendered form (skip when init: form not rendered yet; main flow will apply after renderForm)
        if (!options.skipApply) {
          await this.applyLoadedEntryData();
        }

        // Show success message
        if (window.Swal) {
          const msg = this._getLoadedEntryMessage();
          window.Swal.fire({
            icon: "info",
            title: msg.title,
            text: msg.text,
            timer: 3000,
            showConfirmButton: false,
          });
        }
      }
    } catch (error) {}
  }

  /**
   * Show draft list selector with SweetAlert
   */
  async showDraftList() {
    try {
      const response = await API.listDrafts(this.formId, this.behalfToken);

      if (
        response.status === "ok" &&
        response.drafts &&
        response.drafts.length > 0
      ) {
        // Sort drafts by date DESC (newest first)
        const sortedDrafts = response.drafts.sort((a, b) => {
          const dateA = parseInt(a.date) || 0;
          const dateB = parseInt(b.date) || 0;
          return dateB - dateA; // Descending order (newest first)
        });

        // Format drafts for display
        const draftOptions = sortedDrafts.map((draft, index) => {
          const date = new Date(parseInt(draft.date) * 1000);
          const dateStr = date.toLocaleString();

          // Create preview text from form_data_preview
          let previewText = "";
          if (
            draft.form_data_preview &&
            Object.keys(draft.form_data_preview).length > 0
          ) {
            const previews = Object.entries(draft.form_data_preview)
              .slice(0, 3)
              .map(([key, value]) => `${key}: ${value}`)
              .join(", ");
            previewText = previews ? ` (${previews})` : "";
          }

          return {
            value: draft.entry_id,
            text: `Draft ${index + 1} - ${dateStr}${previewText}`,
          };
        });

        // Show SweetAlert with draft list
        if (window.Swal) {
          const { value: selectedDraftId } = await window.Swal.fire({
            title: "Select Draft to Load",
            input: "select",
            inputOptions: draftOptions.reduce((acc, option) => {
              acc[option.value] = option.text;
              return acc;
            }, {}),
            inputPlaceholder: "Select a draft...",
            showCancelButton: true,
            confirmButtonText: "Load Draft",
            cancelButtonText: "Cancel",
            inputValidator: (value) => {
              if (!value) {
                return "You need to select a draft!";
              }
            },
          });

          if (selectedDraftId) {
            // Load the selected draft
            this.formState.setEditMode(true, selectedDraftId);
            await this.loadEntryData(selectedDraftId);
            await this.applyLoadedEntryData();

            const msg = this._getLoadedEntryMessage();
            window.Swal.fire({
              icon: "success",
              title: msg.title,
              text: msg.text,
              timer: 2000,
              showConfirmButton: false,
            });
          }
        }
      } else {
        // No drafts found
        if (window.Swal) {
          window.Swal.fire({
            icon: "info",
            title: "No Drafts Found",
            text: "No saved drafts were found for this form.",
            timer: 3000,
            showConfirmButton: false,
          });
        }
      }
    } catch (error) {
      if (window.Swal) {
        window.Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load draft list. Please try again.",
          timer: 3000,
          showConfirmButton: false,
        });
      }
    }
  }

  /**
   * Check if draft exists and show resume button if it does
   */
  async checkAndShowResumeDraftButton() {
    try {
      const response = await API.getLastDraft(
        this.formId,
        this.behalfToken,
        !!this.formConfig?.private_draft,
      );
      if (response.status === "ok" && response.data) {
        // Draft exists, show resume button
        this.showResumeDraftButton();
      }
    } catch (error) {}
  }

  /**
   * Show resume draft button in form header
   */
  showResumeDraftButton() {
    if (!this.container) return;

    // Check if button already exists
    if (document.getElementById("resume-draft-button")) return;

    const resumeButton = document.createElement("button");
    resumeButton.id = "resume-draft-button";
    resumeButton.type = "button";
    resumeButton.className = "btn btn-primary resume-draft-button";
    resumeButton.style.cssText = "margin: 10px 0;";
    resumeButton.innerHTML = '<i class="fas fa-file-alt me-2"></i>Resume Draft';

    resumeButton.addEventListener("click", async () => {
      LoadingSpinner.showInitial();
      try {
        await this.loadLastDraft();
      } finally {
        LoadingSpinner.hideInitial();
      }
    });

    // Insert button at the top of the form container
    const form = this.container.querySelector("#tqform");
    if (form && form.parentNode) {
      form.parentNode.insertBefore(resumeButton, form);
    } else if (this.container) {
      this.container.insertBefore(resumeButton, this.container.firstChild);
    }

    // Also add "View All Drafts" button
    const viewAllButton = document.createElement("button");
    viewAllButton.id = "view-all-drafts-button";
    viewAllButton.type = "button";
    viewAllButton.className =
      "btn btn-outline-secondary view-all-drafts-button";
    viewAllButton.style.cssText = "margin: 10px 5px;";
    viewAllButton.innerHTML = '<i class="fas fa-list me-2"></i>View All Drafts';

    viewAllButton.addEventListener("click", async () => {
      await this.showDraftList();
    });

    if (resumeButton.parentNode) {
      resumeButton.parentNode.insertBefore(
        viewAllButton,
        resumeButton.nextSibling,
      );
    }
  }
}